import { Capability } from './capability';

export enum Roles {
  ABSENCE_MANAGER = 'ABSENCE_MANAGER',
  ADMIN = 'ADMIN',
  ALT_MANAGER = 'ALT_MANAGER',
  DEPUTY_MANAGER = 'DEPUTY_MANAGER',
  EMPLOYEE = 'EMPLOYEE',
  EXTERNAL_APP = 'EXTERNAL_APP',
  EXTERNAL_APP_EXTENDED = 'EXTERNAL_APP_EXTENDED',
  EXTERNAL_APP_RESTRICTED = 'EXTERNAL_APP_RESTRICTED',
  GUEST = 'GUEST',
  HR_INITIAL = 'HR_INITIAL',
  HR_ADMIN = 'HR_ADMIN',
  HR_ADMIN_LIMITED = 'HR_ADMIN_LIMITED',
  HR_AUDITOR = 'HR_AUDITOR',
  HR_DEV = 'HR_DEV',
  HR_PEOP = 'HR_PEOP',
  HR_PEOP_DEV = 'HR_PEOP_DEV',
  HR_SALARY = 'HR_SALARY',
  HR_STAFF = 'HR_STAFF',
  IT_ADMIN = 'IT_ADMIN',
  MANAGER = 'MANAGER',
  RECRUITER = 'RECRUITER',
  SUBSTITUTE_ABSENCE_MANAGER = 'SUBSTITUTE_ABSENCE_MANAGER',
}

export enum AdminWorkspaceRoles {
  HR_ADMIN = 'HR_ADMIN',
  HR_STAFF = 'HR_STAFF',
  MANAGER = 'MANAGER',
  ALT_MANAGER = 'ALT_MANAGER',
  DEPUTY_MANAGER = 'DEPUTY_MANAGER',
  EMPLOYEE = 'EMPLOYEE',
}

export const getAdminWorkspaceRolesFromRoles = (
  roles: Roles[],
): AdminWorkspaceRoles[] => {
  const roleStrings = roles.map((r) => r.toString());

  return Object.values(AdminWorkspaceRoles).filter((aRole) =>
    roleStrings.includes(aRole),
  );
};

type RoleTranslationType = {
  [key in Roles]: string;
};

export const roleTranslationKeys: RoleTranslationType = {
  [Roles.ABSENCE_MANAGER]: 'ABSENCE_MANAGER',
  [Roles.ADMIN]: 'ADMIN',
  [Roles.ALT_MANAGER]: 'laAltManager',
  [Roles.DEPUTY_MANAGER]: 'laDeputyManager',
  [Roles.EMPLOYEE]: 'laEmployee',
  [Roles.EXTERNAL_APP]: 'EXTERNAL_APP',
  [Roles.EXTERNAL_APP_EXTENDED]: 'EXTERNAL_APP_EXTENDED',
  [Roles.EXTERNAL_APP_RESTRICTED]: 'EXTERNAL_APP_RESTRICTED',
  [Roles.GUEST]: 'GUEST',
  [Roles.HR_INITIAL]: 'HR_INITIAL',
  [Roles.HR_ADMIN]: 'laHRAdmin',
  [Roles.HR_ADMIN_LIMITED]: 'laHRAdminLimited',
  [Roles.HR_AUDITOR]: 'HR_AUDITOR',
  [Roles.HR_DEV]: 'laHRDev',
  [Roles.HR_PEOP]: 'laHRPeople',
  [Roles.HR_PEOP_DEV]: 'laHRPeopleDev',
  [Roles.HR_SALARY]: 'laHRSalary',
  [Roles.HR_STAFF]: 'laHR',
  [Roles.IT_ADMIN]: 'laITAdmin',
  [Roles.MANAGER]: 'laManager',
  [Roles.RECRUITER]: 'laRecruiter',
  [Roles.SUBSTITUTE_ABSENCE_MANAGER]: 'fOrgUnitSubstituteManager',
};

export const getRoleTranslationKey = (role: Roles): string => {
  return roleTranslationKeys[role];
};

export const getRoleTranslationKeys = (roles: Roles[]): string[] => {
  return roles.map((role) => getRoleTranslationKey(role));
};

export const getUntranslatedRoles = (): Roles[] => {
  return Object.values(Roles).filter(
    (role) => role.toString() === getRoleTranslationKey(role),
  );
};

export const filterUntranslatedRoles = (roles: Roles[]): Roles[] => {
  const untranslated = getUntranslatedRoles();
  return roles.filter((role) => !untranslated.includes(role));
};

export enum RoleScope {
  ALL = 'ALL',
  BELOW = 'BELOW',
  BELOW_ALL = 'BELOW_ALL',
  FILTER = 'FILTER',
  SELF = 'SELF',
}

type RoleCapabilityScope = {
  [Property in RoleScope]?: Capability[];
};

export type RoleCapabilityCombo = {
  name: Roles;
  capabilities: Capability[];
  scope: RoleCapabilityScope;
};

export const getRoleScopeKeys = (scope: RoleCapabilityScope): RoleScope[] => {
  return Object.keys(scope) as RoleScope[];
};

export const ROLE_CAPABILITIES: RoleCapabilityCombo[] = [
  {
    name: Roles.ABSENCE_MANAGER,
    capabilities: [
      Capability.APPROVE_ABSENCE,
      Capability.CREATE_ABSENCE,
      Capability.EDIT_ABSENCE,
      Capability.EDIT_VACATION_BALANCE,
      Capability.HARD_REMOVE_ABSENCE,
      Capability.REMOVE_ABSENCE,
      Capability.REMOVE_VACATION_BALANCE,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_ABSENCE_VACATION,
      Capability.VIEW_VACATION_BALANCE,
    ],
    scope: {
      [RoleScope.BELOW_ALL]: [
        Capability.APPROVE_ABSENCE,
        Capability.CREATE_ABSENCE,
        Capability.EDIT_ABSENCE,
        Capability.EDIT_VACATION_BALANCE,
        Capability.HARD_REMOVE_ABSENCE,
        Capability.REMOVE_ABSENCE,
        Capability.REMOVE_VACATION_BALANCE,
        Capability.VIEW_ABSENCE,
        Capability.VIEW_ABSENCE_VACATION,
        Capability.VIEW_VACATION_BALANCE,
      ],
    },
  },
  {
    name: Roles.ADMIN,
    capabilities: [
      Capability.ACCESS_CONSOLE,
      Capability.APPROVE_ABSENCE,
      Capability.APPROVE_AS_HR,
      Capability.APPROVE_AS_MANAGER,
      Capability.CREATE_ABSENCE,
      Capability.CREATE_ACKNOWLEDGEMENT,
      Capability.CREATE_CANDIDATE,
      Capability.CREATE_CUSTOM_DATA_SCHEMA,
      Capability.CREATE_DOCUMENT,
      Capability.CREATE_EMPL_RESTRICTED,
      Capability.CREATE_EMPLOYEE,
      Capability.CREATE_EMPLOYMENT,
      Capability.CREATE_ENUM,
      Capability.CREATE_NOTIFICATION,
      Capability.CREATE_PLANNING,
      Capability.CREATE_SECONDARY_EMPLOYMENT,
      Capability.CREATE_STRING,
      Capability.CREATE_TASK,
      Capability.CREATE_USER,
      Capability.CREATE_WORKFLOW,
      Capability.EDIT_ABSENCE,
      Capability.EDIT_ACKNOWLEDGEMENT,
      Capability.EDIT_APPCONFIG,
      Capability.EDIT_CUSTOM_DATA,
      Capability.EDIT_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
      Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
      Capability.EDIT_CUSTOM_DATA_JOB_APPLICATION,
      Capability.EDIT_CUSTOM_DATA_SCHEMA,
      Capability.EDIT_DEV_EXPERIENCE_REGULAR,
      Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
      Capability.EDIT_DEV_REGULAR,
      Capability.EDIT_DEV_SENSITIVE,
      Capability.EDIT_DMGR_UNIT,
      Capability.EDIT_DOCUMENT,
      Capability.EDIT_EMP_BASIC,
      Capability.EDIT_EMP_EME,
      Capability.EDIT_EMP_FULL,
      Capability.EDIT_EMP_HOME,
      Capability.EDIT_EMP_OTHER,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_EMPL_BANKACCOUNT,
      Capability.EDIT_EMPL_START,
      Capability.EDIT_EMPLOYMENT,
      Capability.EDIT_EMPLOYMENT_BASIC,
      Capability.EDIT_EMPLOYMENT_DETAIL,
      Capability.EDIT_EMPLOYMENT_INFO,
      Capability.EDIT_ENUM_ADMIN,
      Capability.EDIT_MGR_NOTE,
      Capability.EDIT_NOTIFICATION,
      Capability.EDIT_ORGUNIT,
      Capability.EDIT_PERFORMANCE,
      Capability.EDIT_PLANNING,
      Capability.EDIT_USER_LANGUAGE,
      Capability.EDIT_USER_ROLES,
      Capability.EDIT_USER_SETTING,
      Capability.EDIT_USER_STATUS,
      Capability.EDIT_USER_USERNAME,
      Capability.EDIT_VACATION_BALANCE,
      Capability.EDIT_WORKFLOW,
      Capability.GRAPHQL,
      Capability.HARD_REMOVE_ABSENCE,
      Capability.HARD_REMOVE_COSTCENTER,
      Capability.HARD_REMOVE_JOB_OPENING,
      Capability.HARD_REMOVE_LEGALENTITY,
      Capability.HARD_REMOVE_OFFICE,
      Capability.HARD_REMOVE_ORGUNIT,
      Capability.REMOVE_ABSENCE,
      Capability.REMOVE_ACKNOWLEDGEMENT,
      Capability.REMOVE_CANDIDATE,
      Capability.REMOVE_CUSTOM_DATA_SCHEMA,
      Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
      Capability.REMOVE_DEV_EXPERIENCE_SENSITIVE,
      Capability.REMOVE_DEV_HISTORY,
      Capability.REMOVE_DEV_REGULAR,
      Capability.REMOVE_DEV_SENSITIVE,
      Capability.REMOVE_DOCUMENT,
      Capability.REMOVE_EMP_PROFILE,
      Capability.REMOVE_EMPLOYEE,
      Capability.REMOVE_EMPLOYMENT,
      Capability.REMOVE_ENUM,
      Capability.REMOVE_MGR_NOTE,
      Capability.REMOVE_NOTIFICATION,
      Capability.REMOVE_ORGUNIT,
      Capability.REMOVE_PERF_HISTORY,
      Capability.REMOVE_PERF_REGULAR,
      Capability.REMOVE_PLANNING,
      Capability.REMOVE_POST,
      Capability.REMOVE_STRING,
      Capability.REMOVE_TASK,
      Capability.REMOVE_USER,
      Capability.REMOVE_VACATION_BALANCE,
      Capability.REMOVE_WORKFLOW,
      Capability.RUN_BATCH,
      Capability.RUN_BATCH_BASIC,
      Capability.SEND_EMAIL_HR,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_ACKNOWLEDGEMENT,
      Capability.VIEW_APPCONFIG,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
      Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
      Capability.VIEW_CUSTOM_DATA_SCHEMA,
      Capability.VIEW_DEV_EXPERIENCE,
      Capability.VIEW_DEVELOPMENT,
      Capability.VIEW_DOCUMENT,
      Capability.VIEW_EMP_BASIC,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_EME,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_HOME,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_JOB_OPENING,
      Capability.VIEW_MGR_NOTE,
      Capability.VIEW_NOTIFICATION,
      Capability.VIEW_PERFORMANCE,
      Capability.VIEW_PLANNING,
      Capability.VIEW_REPORT_AMGR,
      Capability.VIEW_REPORT_DMGR,
      Capability.VIEW_REPORT_HR,
      Capability.VIEW_REPORT_HR_SENSITIVE,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_MANAGER,
      Capability.VIEW_REPORT_MANAGER_SENSITIVE,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_TASK,
      Capability.VIEW_VACATION_BALANCE,
      Capability.VIEW_WORKFLOW,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.ACCESS_CONSOLE,
        Capability.APPROVE_ABSENCE,
        Capability.APPROVE_AS_HR,
        Capability.APPROVE_AS_MANAGER,
        Capability.CREATE_ABSENCE,
        Capability.CREATE_ACKNOWLEDGEMENT,
        Capability.CREATE_CANDIDATE,
        Capability.CREATE_CUSTOM_DATA_SCHEMA,
        Capability.CREATE_DOCUMENT,
        Capability.CREATE_EMPL_RESTRICTED,
        Capability.CREATE_EMPLOYEE,
        Capability.CREATE_EMPLOYMENT,
        Capability.CREATE_ENUM,
        Capability.CREATE_NOTIFICATION,
        Capability.CREATE_PLANNING,
        Capability.CREATE_SECONDARY_EMPLOYMENT,
        Capability.CREATE_STRING,
        Capability.CREATE_TASK,
        Capability.CREATE_USER,
        Capability.CREATE_WORKFLOW,
        Capability.EDIT_ABSENCE,
        Capability.EDIT_ACKNOWLEDGEMENT,
        Capability.EDIT_APPCONFIG,
        Capability.EDIT_CUSTOM_DATA,
        Capability.EDIT_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
        Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
        Capability.EDIT_CUSTOM_DATA_JOB_APPLICATION,
        Capability.EDIT_CUSTOM_DATA_SCHEMA,
        Capability.EDIT_DEV_EXPERIENCE_REGULAR,
        Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
        Capability.EDIT_DEV_REGULAR,
        Capability.EDIT_DEV_SENSITIVE,
        Capability.EDIT_DMGR_UNIT,
        Capability.EDIT_DOCUMENT,
        Capability.EDIT_EMP_BASIC,
        Capability.EDIT_EMP_EME,
        Capability.EDIT_EMP_FULL,
        Capability.EDIT_EMP_HOME,
        Capability.EDIT_EMP_OTHER,
        Capability.EDIT_EMP_WORK,
        Capability.EDIT_EMPL_BANKACCOUNT,
        Capability.EDIT_EMPL_START,
        Capability.EDIT_EMPLOYMENT,
        Capability.EDIT_EMPLOYMENT_BASIC,
        Capability.EDIT_EMPLOYMENT_DETAIL,
        Capability.EDIT_EMPLOYMENT_INFO,
        Capability.EDIT_ENUM_ADMIN,
        Capability.EDIT_MGR_NOTE,
        Capability.EDIT_NOTIFICATION,
        Capability.EDIT_ORGUNIT,
        Capability.EDIT_PERFORMANCE,
        Capability.EDIT_PLANNING,
        Capability.EDIT_USER_LANGUAGE,
        Capability.EDIT_USER_ROLES,
        Capability.EDIT_USER_SETTING,
        Capability.EDIT_USER_STATUS,
        Capability.EDIT_USER_USERNAME,
        Capability.EDIT_VACATION_BALANCE,
        Capability.EDIT_WORKFLOW,
        Capability.GRAPHQL,
        Capability.HARD_REMOVE_ABSENCE,
        Capability.HARD_REMOVE_COSTCENTER,
        Capability.HARD_REMOVE_JOB_OPENING,
        Capability.HARD_REMOVE_LEGALENTITY,
        Capability.HARD_REMOVE_OFFICE,
        Capability.HARD_REMOVE_ORGUNIT,
        Capability.REMOVE_ABSENCE,
        Capability.REMOVE_ACKNOWLEDGEMENT,
        Capability.REMOVE_CANDIDATE,
        Capability.REMOVE_CUSTOM_DATA_SCHEMA,
        Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
        Capability.REMOVE_DEV_EXPERIENCE_SENSITIVE,
        Capability.REMOVE_DEV_HISTORY,
        Capability.REMOVE_DEV_REGULAR,
        Capability.REMOVE_DEV_SENSITIVE,
        Capability.REMOVE_DOCUMENT,
        Capability.REMOVE_EMP_PROFILE,
        Capability.REMOVE_EMPLOYEE,
        Capability.REMOVE_EMPLOYMENT,
        Capability.REMOVE_ENUM,
        Capability.REMOVE_MGR_NOTE,
        Capability.REMOVE_NOTIFICATION,
        Capability.REMOVE_ORGUNIT,
        Capability.REMOVE_PERF_HISTORY,
        Capability.REMOVE_PERF_REGULAR,
        Capability.REMOVE_PLANNING,
        Capability.REMOVE_POST,
        Capability.REMOVE_STRING,
        Capability.REMOVE_TASK,
        Capability.REMOVE_USER,
        Capability.REMOVE_VACATION_BALANCE,
        Capability.REMOVE_WORKFLOW,
        Capability.RUN_BATCH,
        Capability.RUN_BATCH_BASIC,
        Capability.SEND_EMAIL_HR,
        Capability.VIEW_ABSENCE,
        Capability.VIEW_ACKNOWLEDGEMENT,
        Capability.VIEW_APPCONFIG,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
        Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
        Capability.VIEW_CUSTOM_DATA_SCHEMA,
        Capability.VIEW_DEV_EXPERIENCE,
        Capability.VIEW_DEVELOPMENT,
        Capability.VIEW_DOCUMENT,
        Capability.VIEW_EMP_BASIC,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_EME,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_HOME,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_JOB_OPENING,
        Capability.VIEW_MGR_NOTE,
        Capability.VIEW_NOTIFICATION,
        Capability.VIEW_PERFORMANCE,
        Capability.VIEW_PLANNING,
        Capability.VIEW_REPORT_AMGR,
        Capability.VIEW_REPORT_DMGR,
        Capability.VIEW_REPORT_HR,
        Capability.VIEW_REPORT_HR_SENSITIVE,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_MANAGER,
        Capability.VIEW_REPORT_MANAGER_SENSITIVE,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
        Capability.VIEW_TASK,
        Capability.VIEW_VACATION_BALANCE,
        Capability.VIEW_WORKFLOW,
      ],
    },
  },
  {
    name: Roles.ALT_MANAGER,
    capabilities: [
      Capability.APPROVE_EVALUATION,
      Capability.APPROVE_GOALS_EVALUATION,
      Capability.ARCHIVE_EVALUATION,
      Capability.ARCHIVE_GOALS_EVALUATION,
      Capability.CREATE_ACKNOWLEDGEMENT,
      Capability.CREATE_EMP_PENDING,
      Capability.CREATE_EMPL_PENDING,
      Capability.CREATE_EQUIPMENT,
      Capability.CREATE_EVALUATION,
      Capability.CREATE_GOALS_EVALUATION,
      Capability.CREATE_JOB_OPENING,
      Capability.CREATE_REPORT_SETTINGS,
      Capability.CREATE_SECONDARY_EMPLOYMENT,
      Capability.CREATE_WORKFLOW,
      Capability.DELETE_REPORT_SETTINGS,
      Capability.DELETE_TIME_CARD,
      Capability.EDIT_ACKNOWLEDGEMENT,
      Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
      Capability.EDIT_DEV_SENSITIVE,
      Capability.EDIT_DMGR_UNIT,
      Capability.EDIT_EMP_FULL,
      Capability.EDIT_EMP_PROFILE_INFO,
      Capability.EDIT_EMPL_BANKACCOUNT,
      Capability.EDIT_EMPLOYMENT,
      Capability.EDIT_EMPLOYMENT_BASIC,
      Capability.EDIT_EMPLOYMENT_DETAIL,
      Capability.EDIT_EMPLOYMENT_INFO,
      Capability.EDIT_EQUIPMENT,
      Capability.EDIT_PLANNING,
      Capability.EDIT_RESP_UNIT,
      Capability.EDIT_WORKFLOW,
      Capability.GRAPHQL,
      Capability.IS_AMGR_RESP,
      Capability.MANAGE_TIME_CARD,
      Capability.REMOVE_EQUIPMENT,
      Capability.REMOVE_PLANNING,
      Capability.SOLAINTEL_CREATE_INDICATOR,
      Capability.SOLAINTEL_DELETE_INDICATOR,
      Capability.SOLAINTEL_EDIT_INDICATOR,
      Capability.SOLAINTEL_VIEW_INDICATOR,
      Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
      Capability.UNREGISTER_FOR_COMPANY_TRAINING,
      Capability.UPDATE_REPORT_SETTINGS,
      Capability.VIEW_ACKNOWLEDGEMENT,
      Capability.VIEW_APPRAISAL_REPORT,
      Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
      Capability.VIEW_CUSTOM_DATA_TIMESHEET,
      Capability.VIEW_DECISION_REPORT,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_ENDED_EDITABLE,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_EVALUATION,
      Capability.VIEW_GOALS_EVALUATION,
      Capability.VIEW_PLANNING,
      Capability.VIEW_REPORT_AMGR,
      Capability.VIEW_REPORT_AMGR_SENSITIVE,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_BASIC,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_MANAGER,
      Capability.VIEW_REPORT_MANAGER_SENSITIVE,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_BASIC,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_TIME_CARD,
      Capability.VIEW_TIMESHEET,
      Capability.VIEW_WORKFLOW,
    ],
    scope: {
      [RoleScope.BELOW_ALL]: [
        Capability.APPROVE_EVALUATION,
        Capability.APPROVE_GOALS_EVALUATION,
        Capability.ARCHIVE_EVALUATION,
        Capability.ARCHIVE_GOALS_EVALUATION,
        Capability.CREATE_ACKNOWLEDGEMENT,
        Capability.CREATE_EMP_PENDING,
        Capability.CREATE_EMPL_PENDING,
        Capability.CREATE_EQUIPMENT,
        Capability.CREATE_EVALUATION,
        Capability.CREATE_GOALS_EVALUATION,
        Capability.CREATE_JOB_OPENING,
        Capability.CREATE_REPORT_SETTINGS,
        Capability.CREATE_SECONDARY_EMPLOYMENT,
        Capability.CREATE_WORKFLOW,
        Capability.DELETE_REPORT_SETTINGS,
        Capability.DELETE_TIME_CARD,
        Capability.EDIT_ACKNOWLEDGEMENT,
        Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
        Capability.EDIT_DEV_SENSITIVE,
        Capability.EDIT_DMGR_UNIT,
        Capability.EDIT_EMP_FULL,
        Capability.EDIT_EMP_PROFILE_INFO,
        Capability.EDIT_EMPL_BANKACCOUNT,
        Capability.EDIT_EMPLOYMENT,
        Capability.EDIT_EMPLOYMENT_BASIC,
        Capability.EDIT_EMPLOYMENT_DETAIL,
        Capability.EDIT_EMPLOYMENT_INFO,
        Capability.EDIT_EQUIPMENT,
        Capability.EDIT_PLANNING,
        Capability.EDIT_RESP_UNIT,
        Capability.EDIT_WORKFLOW,
        Capability.MANAGE_TIME_CARD,
        Capability.REMOVE_EQUIPMENT,
        Capability.REMOVE_PLANNING,
        Capability.SOLAINTEL_CREATE_INDICATOR,
        Capability.SOLAINTEL_DELETE_INDICATOR,
        Capability.SOLAINTEL_EDIT_INDICATOR,
        Capability.SOLAINTEL_VIEW_INDICATOR,
        Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
        Capability.UNREGISTER_FOR_COMPANY_TRAINING,
        Capability.UPDATE_REPORT_SETTINGS,
        Capability.VIEW_ACKNOWLEDGEMENT,
        Capability.VIEW_APPRAISAL_REPORT,
        Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
        Capability.VIEW_CUSTOM_DATA_TIMESHEET,
        Capability.VIEW_DECISION_REPORT,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_EVALUATION,
        Capability.VIEW_GOALS_EVALUATION,
        Capability.VIEW_PLANNING,
        Capability.VIEW_REPORT_AMGR,
        Capability.VIEW_REPORT_AMGR_SENSITIVE,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_LEGENT_BASIC,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_MANAGER,
        Capability.VIEW_REPORT_MANAGER_SENSITIVE,
        Capability.VIEW_REPORT_SETTINGS,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_REPORT_UNIT_BASIC,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
        Capability.VIEW_TIME_CARD,
        Capability.VIEW_TIMESHEET,
        Capability.VIEW_WORKFLOW,
      ],
      [RoleScope.ALL]: [
        Capability.GRAPHQL,
        Capability.IS_AMGR_RESP,
        Capability.VIEW_EMP_ENDED_EDITABLE,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
      ],
    },
  },
  {
    name: Roles.DEPUTY_MANAGER,
    capabilities: [
      Capability.APPROVE_ABSENCE,
      Capability.APPROVE_EVALUATION,
      Capability.APPROVE_GOALS_EVALUATION,
      Capability.ARCHIVE_EVALUATION,
      Capability.ARCHIVE_GOALS_EVALUATION,
      Capability.CREATE_ABSENCE,
      Capability.CREATE_CANDIDATE,
      Capability.CREATE_DECISION,
      Capability.CREATE_DOCUMENT,
      Capability.CREATE_EVALUATION,
      Capability.CREATE_GOALS_EVALUATION,
      Capability.CREATE_JOB_APPLICATION,
      Capability.CREATE_TASK,
      Capability.EDIT_ABSENCE,
      Capability.EDIT_CANDIDATE,
      Capability.EDIT_CUSTOM_DATA,
      Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.EDIT_DEV_EXPERIENCE_REGULAR,
      Capability.EDIT_DEV_REGULAR,
      Capability.EDIT_DOCUMENT,
      Capability.EDIT_EMP_INFO,
      Capability.EDIT_EMP_OFFICE,
      Capability.EDIT_EMP_OTHER,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_JOB_APPLICATION,
      Capability.EDIT_JOB_OPENING,
      Capability.EDIT_MGR_NOTE,
      Capability.EDIT_MGR_NOTE_EMP,
      Capability.EDIT_PERFORMANCE,
      Capability.GRAPHQL,
      Capability.IS_DMGR_RESP,
      Capability.REMOVE_ABSENCE,
      Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
      Capability.REMOVE_DEV_REGULAR,
      Capability.REMOVE_MGR_NOTE,
      Capability.REMOVE_PERF_REGULAR,
      Capability.SEND_GROUP_EMAIL,
      Capability.UPDATE_DECISION,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_APPRAISAL_REPORT,
      Capability.VIEW_CANDIDATE,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_DECISION,
      Capability.VIEW_DECISION_REPORT,
      Capability.VIEW_DEV_EXPERIENCE,
      Capability.VIEW_DEVELOPMENT,
      Capability.VIEW_DMGR_EMPLOYEES,
      Capability.VIEW_DMGR_UNIT,
      Capability.VIEW_DOCUMENT,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_EME,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_EQUIPMENT,
      Capability.VIEW_EVALUATION,
      Capability.VIEW_GOALS_EVALUATION,
      Capability.VIEW_JOB_APPLICATION,
      Capability.VIEW_JOB_OPENING,
      Capability.VIEW_MGR_NOTE,
      Capability.VIEW_MGR_NOTE_EMP,
      Capability.VIEW_PERFORMANCE,
      Capability.VIEW_REPORT_DMGR,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_BASIC,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_BASIC,
      Capability.VIEW_TASK,
      Capability.VIEW_VACATION_BALANCE,
    ],
    scope: {
      [RoleScope.BELOW_ALL]: [
        Capability.APPROVE_ABSENCE,
        Capability.APPROVE_EVALUATION,
        Capability.APPROVE_GOALS_EVALUATION,
        Capability.ARCHIVE_EVALUATION,
        Capability.ARCHIVE_GOALS_EVALUATION,
        Capability.CREATE_ABSENCE,
        Capability.CREATE_CANDIDATE,
        Capability.CREATE_DECISION,
        Capability.CREATE_DOCUMENT,
        Capability.CREATE_EVALUATION,
        Capability.CREATE_GOALS_EVALUATION,
        Capability.CREATE_JOB_APPLICATION,
        Capability.CREATE_TASK,
        Capability.EDIT_ABSENCE,
        Capability.EDIT_CANDIDATE,
        Capability.EDIT_CUSTOM_DATA,
        Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.EDIT_DEV_EXPERIENCE_REGULAR,
        Capability.EDIT_DEV_REGULAR,
        Capability.EDIT_DOCUMENT,
        Capability.EDIT_EMP_INFO,
        Capability.EDIT_EMP_OFFICE,
        Capability.EDIT_EMP_OTHER,
        Capability.EDIT_EMP_WORK,
        Capability.EDIT_JOB_APPLICATION,
        Capability.EDIT_JOB_OPENING,
        Capability.EDIT_MGR_NOTE_EMP,
        Capability.EDIT_PERFORMANCE,
        Capability.REMOVE_ABSENCE,
        Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
        Capability.REMOVE_DEV_REGULAR,
        Capability.REMOVE_PERF_REGULAR,
        Capability.UPDATE_DECISION,
        Capability.VIEW_ABSENCE,
        Capability.VIEW_APPRAISAL_REPORT,
        Capability.VIEW_CANDIDATE,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_DECISION,
        Capability.VIEW_DECISION_REPORT,
        Capability.VIEW_DEV_EXPERIENCE,
        Capability.VIEW_DEVELOPMENT,
        Capability.VIEW_DMGR_EMPLOYEES,
        Capability.VIEW_DMGR_UNIT,
        Capability.VIEW_DOCUMENT,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_EME,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_EQUIPMENT,
        Capability.VIEW_EVALUATION,
        Capability.VIEW_GOALS_EVALUATION,
        Capability.VIEW_JOB_APPLICATION,
        Capability.VIEW_JOB_OPENING,
        Capability.VIEW_MGR_NOTE_EMP,
        Capability.VIEW_PERFORMANCE,
        Capability.VIEW_REPORT_DMGR,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_LEGENT_BASIC,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_REPORT_UNIT_BASIC,
        Capability.VIEW_TASK,
        Capability.VIEW_VACATION_BALANCE,
      ],
      [RoleScope.SELF]: [
        Capability.EDIT_MGR_NOTE,
        Capability.REMOVE_MGR_NOTE,
        Capability.VIEW_MGR_NOTE,
      ],
      [RoleScope.ALL]: [
        Capability.GRAPHQL,
        Capability.IS_DMGR_RESP,
        Capability.SEND_GROUP_EMAIL,
      ],
    },
  },
  {
    name: Roles.EMPLOYEE,
    capabilities: [
      Capability.APPROVE_EVALUATION,
      Capability.APPROVE_GOALS_EVALUATION,
      Capability.ARCHIVE_EVALUATION,
      Capability.ARCHIVE_GOALS_EVALUATION,
      Capability.AUTHENTICATED,
      Capability.CREATE_ABSENCE,
      Capability.CREATE_CLIENTERROR,
      Capability.CREATE_DOCUMENT,
      Capability.CREATE_EVALUATION,
      Capability.CREATE_GOALS_EVALUATION,
      Capability.CREATE_SURVEY,
      Capability.CREATE_TASK,
      Capability.CREATE_WORKFLOW,
      Capability.DELETE_TIME_CARD,
      Capability.EDIT_ABSENCE,
      Capability.EDIT_CUSTOM_DATA_COST_CENTER,
      Capability.EDIT_DEV_EXPERIENCE_REGULAR,
      Capability.EDIT_DEV_REGULAR,
      Capability.EDIT_DOCUMENT,
      Capability.EDIT_EMP_EME,
      Capability.EDIT_EMP_HOME,
      Capability.EDIT_EMP_INFO,
      Capability.EDIT_EMP_OFFICE,
      Capability.EDIT_EMP_OTHER,
      Capability.EDIT_EMP_PROFILE,
      Capability.EDIT_EMP_PROFILE_INFO,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_EMPL_BANKACCOUNT,
      Capability.EDIT_EMPLOYMENT_INFO,
      Capability.EDIT_NOTIFICATION,
      Capability.EDIT_PERFORMANCE,
      Capability.EDIT_TASK,
      Capability.EDIT_TASK_STATE,
      Capability.EDIT_USER_LANGUAGE,
      Capability.EDIT_USER_PASSWORD,
      Capability.EDIT_USER_SETTING,
      Capability.EDIT_WORKFLOW,
      Capability.GRAPHQL,
      Capability.MANAGE_JOB_REQUIREMENT,
      Capability.MANAGE_TIME_CARD,
      Capability.RAISE_SUPPORT_REQUEST,
      Capability.REGISTER_FOR_COMPANY_TRAINING,
      Capability.REMOVE_ABSENCE,
      Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
      Capability.REMOVE_DEV_REGULAR,
      Capability.REMOVE_EMP_PROFILE,
      Capability.SEND_EMAIL_HR,
      Capability.SOLAPULSE_ANSWER_SURVEY,
      Capability.SOLAPULSE_VIEW_ACTIVE_SURVEY,
      Capability.SOLAPULSE_VIEW_NOTIFICATION,
      Capability.SOLAPULSE_VIEW_RESULTS,
      Capability.UNREGISTER_FOR_COMPANY_TRAINING,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_ABSENCE_VACATION,
      Capability.VIEW_ACKNOWLEDGEMENT,
      Capability.VIEW_APPRAISAL,
      Capability.VIEW_COMPANY,
      Capability.VIEW_COMPANY_FILE,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
      Capability.VIEW_CUSTOM_DATA_COST_CENTER,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
      Capability.VIEW_CUSTOM_DATA_SCHEMA,
      Capability.VIEW_CUSTOM_DATA_TIMESHEET,
      Capability.VIEW_DECISION,
      Capability.VIEW_DEV_EXPERIENCE,
      Capability.VIEW_DEVELOPMENT,
      Capability.VIEW_DOC_APPROVAL,
      Capability.VIEW_DOCUMENT,
      Capability.VIEW_EARLY_INVOLVEMENT,
      Capability.VIEW_EMP_BASIC,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_BY_ROLE,
      Capability.VIEW_EMP_EME,
      Capability.VIEW_EMP_FULL,
      Capability.VIEW_EMP_HOME,
      Capability.VIEW_EMP_OTHER,
      Capability.VIEW_EMP_PROFILE,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMP_USERNAME,
      Capability.VIEW_EMP_WORK,
      Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_ENUM_SCORE_GROUP,
      Capability.VIEW_EQUIPMENT,
      Capability.VIEW_EVALUATION,
      Capability.VIEW_GOALS,
      Capability.VIEW_GOALS_EVALUATION,
      Capability.VIEW_HOLIDAY,
      Capability.VIEW_JOB_REQUIREMENT,
      Capability.VIEW_NOTIFICATION,
      Capability.VIEW_OFFICE,
      Capability.VIEW_ORGUNIT,
      Capability.VIEW_PERFORMANCE,
      Capability.VIEW_POST_PUB,
      Capability.VIEW_REPORT_COMPANY_LIMITED,
      Capability.VIEW_REPORT_EMPLOYEE,
      Capability.VIEW_REPORT_LEGENT_LIMITED,
      Capability.VIEW_REPORT_UNIT_LIMITED,
      Capability.VIEW_RESP_UNIT,
      Capability.VIEW_STRING,
      Capability.VIEW_SUMMARY_COMPANY,
      Capability.VIEW_SUMMARY_LEGENT,
      Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
      Capability.VIEW_TARGETED_COMPANY_TRAININGS,
      Capability.VIEW_TASK,
      Capability.VIEW_TIME_CARD,
      Capability.VIEW_TIMESHEET,
      Capability.VIEW_TIMESHEET_SETTINGS,
      Capability.VIEW_TRANSLATIONS,
      Capability.VIEW_TREE,
      Capability.VIEW_USER,
      Capability.VIEW_USER_FULL,
      Capability.VIEW_USER_LOGIN_INFO,
      Capability.VIEW_VACATION_BALANCE,
      Capability.VIEW_WORKFLOW,
      Capability.VIEW_WORKLIST,
    ],
    scope: {
      [RoleScope.SELF]: [
        Capability.APPROVE_EVALUATION,
        Capability.APPROVE_GOALS_EVALUATION,
        Capability.ARCHIVE_EVALUATION,
        Capability.ARCHIVE_GOALS_EVALUATION,
        Capability.CREATE_ABSENCE,
        Capability.CREATE_DOCUMENT,
        Capability.CREATE_EVALUATION,
        Capability.CREATE_GOALS_EVALUATION,
        Capability.CREATE_TASK,
        Capability.CREATE_WORKFLOW,
        Capability.DELETE_TIME_CARD,
        Capability.EDIT_ABSENCE,
        Capability.EDIT_DEV_EXPERIENCE_REGULAR,
        Capability.EDIT_DEV_REGULAR,
        Capability.EDIT_DOCUMENT,
        Capability.EDIT_EMP_EME,
        Capability.EDIT_EMP_HOME,
        Capability.EDIT_EMP_INFO,
        Capability.EDIT_EMP_OTHER,
        Capability.EDIT_EMP_PROFILE,
        Capability.EDIT_EMP_PROFILE_INFO,
        Capability.EDIT_EMPL_BANKACCOUNT,
        Capability.EDIT_EMPLOYMENT_INFO,
        Capability.EDIT_NOTIFICATION,
        Capability.EDIT_PERFORMANCE,
        Capability.EDIT_USER_LANGUAGE,
        Capability.EDIT_USER_PASSWORD,
        Capability.EDIT_USER_SETTING,
        Capability.EDIT_WORKFLOW,
        Capability.MANAGE_JOB_REQUIREMENT,
        Capability.MANAGE_TIME_CARD,
        Capability.RAISE_SUPPORT_REQUEST,
        Capability.REGISTER_FOR_COMPANY_TRAINING,
        Capability.REMOVE_ABSENCE,
        Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
        Capability.REMOVE_DEV_REGULAR,
        Capability.REMOVE_EMP_PROFILE,
        Capability.SEND_EMAIL_HR,
        Capability.SOLAPULSE_VIEW_NOTIFICATION,
        Capability.UNREGISTER_FOR_COMPANY_TRAINING,
        Capability.VIEW_ABSENCE,
        Capability.VIEW_ACKNOWLEDGEMENT,
        Capability.VIEW_APPRAISAL,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
        Capability.VIEW_CUSTOM_DATA_TIMESHEET,
        Capability.VIEW_DECISION,
        Capability.VIEW_DEV_EXPERIENCE,
        Capability.VIEW_DEVELOPMENT,
        Capability.VIEW_DOC_APPROVAL,
        Capability.VIEW_DOCUMENT,
        Capability.VIEW_EARLY_INVOLVEMENT,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_EME,
        Capability.VIEW_EMP_HOME,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMP_USERNAME,
        Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_EQUIPMENT,
        Capability.VIEW_EVALUATION,
        Capability.VIEW_GOALS,
        Capability.VIEW_GOALS_EVALUATION,
        Capability.VIEW_JOB_REQUIREMENT,
        Capability.VIEW_NOTIFICATION,
        Capability.VIEW_PERFORMANCE,
        Capability.VIEW_TARGETED_COMPANY_TRAININGS,
        Capability.VIEW_TASK,
        Capability.VIEW_TIME_CARD,
        Capability.VIEW_TIMESHEET,
        Capability.VIEW_USER,
        Capability.VIEW_USER_FULL,
        Capability.VIEW_USER_LOGIN_INFO,
        Capability.VIEW_VACATION_BALANCE,
        Capability.VIEW_WORKFLOW,
      ],
      [RoleScope.ALL]: [
        Capability.AUTHENTICATED,
        Capability.CREATE_CLIENTERROR,
        Capability.CREATE_SURVEY,
        Capability.EDIT_CUSTOM_DATA_COST_CENTER,
        Capability.EDIT_TASK,
        Capability.EDIT_TASK_STATE,
        Capability.GRAPHQL,
        Capability.SOLAPULSE_ANSWER_SURVEY,
        Capability.SOLAPULSE_VIEW_ACTIVE_SURVEY,
        Capability.SOLAPULSE_VIEW_RESULTS,
        Capability.VIEW_ABSENCE_VACATION,
        Capability.VIEW_COMPANY,
        Capability.VIEW_COMPANY_FILE,
        Capability.VIEW_CUSTOM_DATA_COST_CENTER,
        Capability.VIEW_CUSTOM_DATA_SCHEMA,
        Capability.VIEW_EMP_BY_ROLE,
        Capability.VIEW_EMP_FULL,
        Capability.VIEW_EMP_OTHER,
        Capability.VIEW_EMP_PROFILE,
        Capability.VIEW_EMP_WORK,
        Capability.VIEW_ENUM_SCORE_GROUP,
        Capability.VIEW_HOLIDAY,
        Capability.VIEW_OFFICE,
        Capability.VIEW_ORGUNIT,
        Capability.VIEW_POST_PUB,
        Capability.VIEW_REPORT_COMPANY_LIMITED,
        Capability.VIEW_REPORT_EMPLOYEE,
        Capability.VIEW_REPORT_LEGENT_LIMITED,
        Capability.VIEW_REPORT_UNIT_LIMITED,
        Capability.VIEW_RESP_UNIT,
        Capability.VIEW_STRING,
        Capability.VIEW_SUMMARY_COMPANY,
        Capability.VIEW_SUMMARY_LEGENT,
        Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
        Capability.VIEW_TIMESHEET_SETTINGS,
        Capability.VIEW_TRANSLATIONS,
        Capability.VIEW_TREE,
        Capability.VIEW_WORKLIST,
      ],
      [RoleScope.FILTER]: [
        Capability.EDIT_EMP_OFFICE,
        Capability.EDIT_EMP_WORK,
        Capability.VIEW_EMP_BASIC,
      ],
    },
  },
  {
    name: Roles.EXTERNAL_APP,
    capabilities: [
      Capability.GRAPHQL,
      Capability.RUN_BATCH_BASIC,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_COMPANY_SENSITIVE,
      Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.GRAPHQL,
        Capability.RUN_BATCH_BASIC,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
        Capability.VIEW_REPORT_COMPANY_SENSITIVE,
        Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
      ],
    },
  },
  {
    name: Roles.EXTERNAL_APP_EXTENDED,
    capabilities: [
      Capability.CREATE_REPORT_SETTINGS,
      Capability.DELETE_REPORT_SETTINGS,
      Capability.GRAPHQL,
      Capability.UPDATE_REPORT_SETTINGS,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_COMPANY_LIMITED,
      Capability.VIEW_REPORT_COMPANY_SENSITIVE,
      Capability.VIEW_REPORT_EMPLOYEE,
      Capability.VIEW_REPORT_HR,
      Capability.VIEW_REPORT_HR_SENSITIVE,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_BASIC,
      Capability.VIEW_REPORT_LEGENT_LIMITED,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_BASIC,
      Capability.VIEW_REPORT_UNIT_LIMITED,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_SUMMARY_LEGENT,
      Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
    ],
    scope: {
      [RoleScope.BELOW]: [
        Capability.CREATE_REPORT_SETTINGS,
        Capability.DELETE_REPORT_SETTINGS,
        Capability.UPDATE_REPORT_SETTINGS,
        Capability.VIEW_REPORT_SETTINGS,
      ],
      [RoleScope.ALL]: [
        Capability.GRAPHQL,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
        Capability.VIEW_REPORT_COMPANY_LIMITED,
        Capability.VIEW_REPORT_COMPANY_SENSITIVE,
        Capability.VIEW_REPORT_EMPLOYEE,
        Capability.VIEW_REPORT_HR,
        Capability.VIEW_REPORT_HR_SENSITIVE,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_LEGENT_BASIC,
        Capability.VIEW_REPORT_LEGENT_LIMITED,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_REPORT_UNIT_BASIC,
        Capability.VIEW_REPORT_UNIT_LIMITED,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
        Capability.VIEW_SUMMARY_LEGENT,
        Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
      ],
    },
  },
  {
    name: Roles.EXTERNAL_APP_RESTRICTED,
    capabilities: [
      Capability.GRAPHQL,
      Capability.RUN_BATCH_BASIC,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.GRAPHQL,
        Capability.RUN_BATCH_BASIC,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
      ],
    },
  },
  {
    name: Roles.GUEST,
    capabilities: [
      Capability.CREATE_JOB_APP_PUBLIC,
      Capability.EDIT_DOC_APPROVAL_STATUS,
      Capability.GET_TOKEN,
      Capability.GRAPHQL,
      Capability.LOGIN,
      Capability.LOGOUT,
      Capability.REGISTER_FOR_PUBLIC_COMPANY_TRAINING,
      Capability.RESET,
      Capability.VIEW_COMPANY_NAME,
      Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
      Capability.VIEW_DOC_APPROVAL_FILE,
      Capability.VIEW_ENUM,
      Capability.VIEW_JOB_OPENING_PUBLIC,
      Capability.VIEW_MODULE,
      Capability.VIEW_PUBLIC_COMPANY_TRAININGS,
      Capability.VIEW_PUBLIC_DOCUMENT,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.CREATE_JOB_APP_PUBLIC,
        Capability.EDIT_DOC_APPROVAL_STATUS,
        Capability.GET_TOKEN,
        Capability.GRAPHQL,
        Capability.REGISTER_FOR_PUBLIC_COMPANY_TRAINING,
        Capability.VIEW_COMPANY_NAME,
        Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
        Capability.VIEW_DOC_APPROVAL_FILE,
        Capability.VIEW_ENUM,
        Capability.VIEW_JOB_OPENING_PUBLIC,
        Capability.VIEW_MODULE,
        Capability.VIEW_PUBLIC_COMPANY_TRAININGS,
        Capability.VIEW_PUBLIC_DOCUMENT,
      ],
      [RoleScope.SELF]: [Capability.LOGIN, Capability.LOGOUT, Capability.RESET],
    },
  },
  {
    name: Roles.HR_ADMIN,
    capabilities: [
      Capability.APPROVE_GOALS_EVALUATION,
      Capability.ARCHIVE_GOALS_EVALUATION,
      Capability.CREATE_EARLY_INVOLVEMENT,
      Capability.CREATE_GOALS,
      Capability.CREATE_GOALS_EVALUATION,
      Capability.CREATE_ORGUNIT,
      Capability.CREATE_POST,
      Capability.CREATE_REPORT_SETTINGS,
      Capability.CREATE_USER_ENUM,
      Capability.DELETE_EARLY_INVOLVEMENT,
      Capability.DELETE_GOALS,
      Capability.DELETE_REPORT_SETTINGS,
      Capability.DELETE_TIME_CARD,
      Capability.DELETE_TIMESHEET,
      Capability.EDIT_COMPANY,
      Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
      Capability.EDIT_DEV_SENSITIVE,
      Capability.EDIT_EMP_BASIC,
      Capability.EDIT_EMP_CONTRACT_NUMBER,
      Capability.EDIT_EMP_FULL,
      Capability.EDIT_EMP_NUMBER,
      Capability.EDIT_EMPLOYMENT,
      Capability.EDIT_EMPLOYMENT_BASIC,
      Capability.EDIT_EMPLOYMENT_DETAIL,
      Capability.EDIT_HR_UNIT,
      Capability.EDIT_POST,
      Capability.EDIT_RESP_UNIT,
      Capability.GRAPHQL,
      Capability.IS_HRADMIN_RESP,
      Capability.MANAGE_BACKUP,
      Capability.MANAGE_TIME_CARD,
      Capability.REMOVE_EMPLOYEES_BULK,
      Capability.REMOVE_JOB_OPENING,
      Capability.REMOVE_USER_ENUM,
      Capability.UPDATE_EARLY_INVOLVEMENT,
      Capability.UPDATE_GOALS,
      Capability.UPDATE_REPORT_SETTINGS,
      Capability.VIEW_CUSTOM_DATA_TIMESHEET,
      Capability.VIEW_EARLY_INVOLVEMENT,
      Capability.VIEW_GOALS,
      Capability.VIEW_GOALS_EVALUATION,
      Capability.VIEW_GOALS_REPORT,
      Capability.VIEW_POST,
      Capability.VIEW_REPORT_HR_APPRAISAL,
      Capability.VIEW_REPORT_HR_DECISION,
      Capability.VIEW_REPORT_HR_EARLY_INVOLVEMENT,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_ROLE_CAPABILITY,
      Capability.VIEW_TIME_CARD,
      Capability.VIEW_TIMESHEET,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.APPROVE_GOALS_EVALUATION,
        Capability.ARCHIVE_GOALS_EVALUATION,
        Capability.CREATE_EARLY_INVOLVEMENT,
        Capability.CREATE_GOALS,
        Capability.CREATE_GOALS_EVALUATION,
        Capability.CREATE_ORGUNIT,
        Capability.CREATE_POST,
        Capability.CREATE_REPORT_SETTINGS,
        Capability.CREATE_USER_ENUM,
        Capability.DELETE_EARLY_INVOLVEMENT,
        Capability.DELETE_GOALS,
        Capability.DELETE_REPORT_SETTINGS,
        Capability.DELETE_TIME_CARD,
        Capability.DELETE_TIMESHEET,
        Capability.EDIT_COMPANY,
        Capability.EDIT_HR_UNIT,
        Capability.EDIT_POST,
        Capability.EDIT_RESP_UNIT,
        Capability.GRAPHQL,
        Capability.IS_HRADMIN_RESP,
        Capability.MANAGE_BACKUP,
        Capability.MANAGE_TIME_CARD,
        Capability.REMOVE_JOB_OPENING,
        Capability.REMOVE_USER_ENUM,
        Capability.UPDATE_EARLY_INVOLVEMENT,
        Capability.UPDATE_GOALS,
        Capability.UPDATE_REPORT_SETTINGS,
        Capability.VIEW_CUSTOM_DATA_TIMESHEET,
        Capability.VIEW_EARLY_INVOLVEMENT,
        Capability.VIEW_GOALS,
        Capability.VIEW_GOALS_EVALUATION,
        Capability.VIEW_GOALS_REPORT,
        Capability.VIEW_POST,
        Capability.VIEW_REPORT_HR_APPRAISAL,
        Capability.VIEW_REPORT_HR_DECISION,
        Capability.VIEW_REPORT_HR_EARLY_INVOLVEMENT,
        Capability.VIEW_REPORT_SETTINGS,
        Capability.VIEW_ROLE_CAPABILITY,
        Capability.VIEW_TIME_CARD,
        Capability.VIEW_TIMESHEET,
      ],
      [RoleScope.SELF]: [
        Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
        Capability.EDIT_DEV_SENSITIVE,
        Capability.EDIT_EMP_BASIC,
        Capability.EDIT_EMP_CONTRACT_NUMBER,
        Capability.EDIT_EMP_FULL,
        Capability.EDIT_EMP_NUMBER,
        Capability.EDIT_EMPLOYMENT,
        Capability.EDIT_EMPLOYMENT_BASIC,
        Capability.EDIT_EMPLOYMENT_DETAIL,
      ],
      [RoleScope.BELOW_ALL]: [Capability.REMOVE_EMPLOYEES_BULK],
    },
  },
  {
    name: Roles.HR_AUDITOR,
    capabilities: [
      Capability.SOLAINTEL_VIEW_INDICATOR,
      Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
      Capability.SOLAPULSE_VIEW_SURVEY,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_ACKNOWLEDGEMENT,
      Capability.VIEW_ALL_COMPANY_TRAININGS,
      Capability.VIEW_APPRAISAL,
      Capability.VIEW_APPRAISAL_REPORT,
      Capability.VIEW_BONUS_CALC_RULE,
      Capability.VIEW_CANDIDATE,
      Capability.VIEW_COMPANY_SENSITIVE,
      Capability.VIEW_COMPANY_TRAINING_PARTICIPANTS,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
      Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
      Capability.VIEW_CUSTOM_DATA_SCHEMA,
      Capability.VIEW_DEV_EXPERIENCE,
      Capability.VIEW_DEVELOPMENT,
      Capability.VIEW_DMGR_EMPLOYEES,
      Capability.VIEW_DMGR_UNIT,
      Capability.VIEW_DOC_APPROVAL,
      Capability.VIEW_DOCUMENT,
      Capability.VIEW_EARLY_INVOLVEMENT,
      Capability.VIEW_EMP_BASIC,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_EME,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_HOME,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMP_USERNAME,
      Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_EQUIPMENT,
      Capability.VIEW_EVALUATION,
      Capability.VIEW_HR_UNIT,
      Capability.VIEW_JOB_APPLICATION,
      Capability.VIEW_JOB_OPENING,
      Capability.VIEW_MAIL_TEMPL,
      Capability.VIEW_NOTIFICATION,
      Capability.VIEW_PERFORMANCE,
      Capability.VIEW_PLANNING,
      Capability.VIEW_POST,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_COMPANY_SENSITIVE,
      Capability.VIEW_REPORT_HR,
      Capability.VIEW_REPORT_HR_SENSITIVE,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_BASIC,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_BASIC,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_ROLE_CAPABILITY,
      Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
      Capability.VIEW_SUMMARY_LEGENT,
      Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
      Capability.VIEW_TARGETED_COMPANY_TRAININGS,
      Capability.VIEW_TASK,
      Capability.VIEW_USER,
      Capability.VIEW_USER_FULL,
      Capability.VIEW_VACATION_BALANCE,
      Capability.VIEW_WORKFLOW,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.SOLAINTEL_VIEW_INDICATOR,
        Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
        Capability.SOLAPULSE_VIEW_SURVEY,
        Capability.VIEW_ALL_COMPANY_TRAININGS,
        Capability.VIEW_APPRAISAL,
        Capability.VIEW_APPRAISAL_REPORT,
        Capability.VIEW_BONUS_CALC_RULE,
        Capability.VIEW_CANDIDATE,
        Capability.VIEW_COMPANY_SENSITIVE,
        Capability.VIEW_COMPANY_TRAINING_PARTICIPANTS,
        Capability.VIEW_DOC_APPROVAL,
        Capability.VIEW_EARLY_INVOLVEMENT,
        Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
        Capability.VIEW_EVALUATION,
        Capability.VIEW_HR_UNIT,
        Capability.VIEW_MAIL_TEMPL,
        Capability.VIEW_POST,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
        Capability.VIEW_REPORT_COMPANY_SENSITIVE,
        Capability.VIEW_REPORT_LEGENT_BASIC,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_SETTINGS,
        Capability.VIEW_REPORT_UNIT_BASIC,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
        Capability.VIEW_ROLE_CAPABILITY,
        Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
        Capability.VIEW_SUMMARY_LEGENT,
        Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
        Capability.VIEW_TARGETED_COMPANY_TRAININGS,
        Capability.VIEW_USER,
      ],
      [RoleScope.BELOW_ALL]: [
        Capability.VIEW_ABSENCE,
        Capability.VIEW_ACKNOWLEDGEMENT,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
        Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
        Capability.VIEW_CUSTOM_DATA_SCHEMA,
        Capability.VIEW_DEV_EXPERIENCE,
        Capability.VIEW_DEVELOPMENT,
        Capability.VIEW_DMGR_EMPLOYEES,
        Capability.VIEW_DMGR_UNIT,
        Capability.VIEW_DOCUMENT,
        Capability.VIEW_EMP_BASIC,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_EME,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_HOME,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMP_USERNAME,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_EQUIPMENT,
        Capability.VIEW_JOB_APPLICATION,
        Capability.VIEW_JOB_OPENING,
        Capability.VIEW_NOTIFICATION,
        Capability.VIEW_PERFORMANCE,
        Capability.VIEW_PLANNING,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_TASK,
        Capability.VIEW_USER_FULL,
        Capability.VIEW_VACATION_BALANCE,
        Capability.VIEW_WORKFLOW,
      ],
      [RoleScope.SELF]: [
        Capability.VIEW_REPORT_HR,
        Capability.VIEW_REPORT_HR_SENSITIVE,
      ],
    },
  },
  {
    name: Roles.HR_DEV,
    capabilities: [
      Capability.APPROVE_ABSENCE,
      Capability.APPROVE_AS_HR,
      Capability.APPROVE_EVALUATION,
      Capability.APPROVE_GOALS_EVALUATION,
      Capability.ARCHIVE_EVALUATION,
      Capability.ARCHIVE_GOALS_EVALUATION,
      Capability.CREATE_ABSENCE,
      Capability.CREATE_ACKNOWLEDGEMENT,
      Capability.CREATE_APPRAISAL,
      Capability.CREATE_CANDIDATE,
      Capability.CREATE_CUSTOM_DATA_SCHEMA,
      Capability.CREATE_DECISION,
      Capability.CREATE_DEV_COMPANY,
      Capability.CREATE_DOCUMENT,
      Capability.CREATE_EARLY_INVOLVEMENT,
      Capability.CREATE_EQUIPMENT,
      Capability.CREATE_EVALUATION,
      Capability.CREATE_GOALS,
      Capability.CREATE_GOALS_EVALUATION,
      Capability.CREATE_HOLIDAY,
      Capability.CREATE_JOB_APPLICATION,
      Capability.CREATE_JOB_OPENING,
      Capability.CREATE_MAIL_TEMPL,
      Capability.CREATE_NOTIFICATION,
      Capability.CREATE_PLANNING,
      Capability.CREATE_TASK,
      Capability.CREATE_TIMESHEET_SETTINGS,
      Capability.CREATE_USER,
      Capability.CREATE_WORKLIST,
      Capability.DELETE_APPRAISAL,
      Capability.DELETE_EARLY_INVOLVEMENT,
      Capability.DELETE_GOALS,
      Capability.DELETE_TIME_CARD,
      Capability.DELETE_TIMESHEET,
      Capability.EDIT_ABSENCE,
      Capability.EDIT_ACKNOWLEDGEMENT,
      Capability.EDIT_CANDIDATE,
      Capability.EDIT_COMPANY_CONFIG,
      Capability.EDIT_COMPANY_FILE,
      Capability.EDIT_COMPANY_TRAINING_PARTICIPANT,
      Capability.EDIT_CUSTOM_DATA,
      Capability.EDIT_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
      Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.EDIT_CUSTOM_DATA_JOB_APPLICATION,
      Capability.EDIT_CUSTOM_DATA_SCHEMA,
      Capability.EDIT_DEV_COMPANY,
      Capability.EDIT_DEV_EXPERIENCE_REGULAR,
      Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
      Capability.EDIT_DEV_REGULAR,
      Capability.EDIT_DEV_SENSITIVE,
      Capability.EDIT_DMGR_UNIT,
      Capability.EDIT_DOCUMENT,
      Capability.EDIT_EMP_BASIC,
      Capability.EDIT_EMP_CONTRACT_NUMBER,
      Capability.EDIT_EMP_EME,
      Capability.EDIT_EMP_FULL,
      Capability.EDIT_EMP_HOME,
      Capability.EDIT_EMP_INFO,
      Capability.EDIT_EMP_NUMBER,
      Capability.EDIT_EMP_OFFICE,
      Capability.EDIT_EMP_OTHER,
      Capability.EDIT_EMP_PROFILE,
      Capability.EDIT_EMP_PROFILE_INFO,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_EMPL_START,
      Capability.EDIT_EMPLOYMENT_BASIC,
      Capability.EDIT_EMPLOYMENT_DETAIL,
      Capability.EDIT_EMPLOYMENT_INFO,
      Capability.EDIT_ENUM,
      Capability.EDIT_EQUIPMENT,
      Capability.EDIT_HOLIDAY,
      Capability.EDIT_JOB_APPLICATION,
      Capability.EDIT_JOB_OPENING,
      Capability.EDIT_NOTIFICATION,
      Capability.EDIT_PERFORMANCE,
      Capability.EDIT_PLANNING,
      Capability.EDIT_RESP_UNIT,
      Capability.EDIT_STRING,
      Capability.EDIT_TIMESHEET_SETTINGS,
      Capability.EDIT_USER_LANGUAGE,
      Capability.EDIT_USER_SETTING,
      Capability.EDIT_VACATION_BALANCE,
      Capability.EDIT_WORKFLOW,
      Capability.EDIT_WORKLIST,
      Capability.GRAPHQL,
      Capability.IS_HR_RESP,
      Capability.MANAGE_COMPANY_TRAINING_PARTICIPANTS,
      Capability.MANAGE_COMPANY_TRAININGS,
      Capability.MANAGE_ENUM_SCORE_GROUP,
      Capability.MANAGE_JOB_REQUIREMENT,
      Capability.MANAGE_TIME_CARD,
      Capability.REMOVE_ABSENCE,
      Capability.REMOVE_CUSTOM_DATA_SCHEMA,
      Capability.REMOVE_DEV_COMPANY,
      Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
      Capability.REMOVE_DEV_REGULAR,
      Capability.REMOVE_DOCUMENT,
      Capability.REMOVE_EQUIPMENT,
      Capability.REMOVE_HOLIDAY,
      Capability.REMOVE_JOB_APPLICATION,
      Capability.REMOVE_JOB_OPENING,
      Capability.REMOVE_MAIL_TEMPL,
      Capability.REMOVE_NOTIFICATION,
      Capability.REMOVE_PERF_REGULAR,
      Capability.REMOVE_PLANNING,
      Capability.REMOVE_TASK,
      Capability.REMOVE_USER,
      Capability.SEND_GROUP_EMAIL,
      Capability.SOLAPULSE_CREATE_SURVEY,
      Capability.SOLAPULSE_MANAGE_SURVEY,
      Capability.SOLAPULSE_VIEW_SURVEY,
      Capability.UNREGISTER_FOR_COMPANY_TRAINING,
      Capability.UPDATE_APPRAISAL,
      Capability.UPDATE_DECISION,
      Capability.UPDATE_EARLY_INVOLVEMENT,
      Capability.UPDATE_GOALS,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_ACKNOWLEDGEMENT,
      Capability.VIEW_ALL_COMPANY_TRAININGS,
      Capability.VIEW_APPRAISAL,
      Capability.VIEW_APPRAISAL_REPORT,
      Capability.VIEW_CANDIDATE,
      Capability.VIEW_COMPANY_TRAINING_PARTICIPANTS,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
      Capability.VIEW_CUSTOM_DATA_TIMESHEET,
      Capability.VIEW_DECISION,
      Capability.VIEW_DECISION_REPORT,
      Capability.VIEW_DEV_EXPERIENCE,
      Capability.VIEW_DEVELOPMENT,
      Capability.VIEW_DMGR_EMPLOYEES,
      Capability.VIEW_DMGR_UNIT,
      Capability.VIEW_DOCUMENT,
      Capability.VIEW_EARLY_INVOLVEMENT,
      Capability.VIEW_EARLY_INVOLVEMENT_REPORT,
      Capability.VIEW_EMP_BASIC,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_EME,
      Capability.VIEW_EMP_ENDED_EDITABLE,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_HOME,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMP_USERNAME,
      Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_ENUM_SCORE_GROUP,
      Capability.VIEW_EQUIPMENT,
      Capability.VIEW_EVALUATION,
      Capability.VIEW_GOALS,
      Capability.VIEW_GOALS_EVALUATION,
      Capability.VIEW_HR_UNIT,
      Capability.VIEW_JOB_APPLICATION,
      Capability.VIEW_JOB_OPENING,
      Capability.VIEW_JOB_REQUIREMENT,
      Capability.VIEW_NOTIFICATION,
      Capability.VIEW_PERFORMANCE,
      Capability.VIEW_PLANNING,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_HR,
      Capability.VIEW_REPORT_HR_APPRAISAL,
      Capability.VIEW_REPORT_HR_DECISION,
      Capability.VIEW_REPORT_HR_EARLY_INVOLVEMENT,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_BASIC,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_BASIC,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_TARGETED_COMPANY_TRAININGS,
      Capability.VIEW_TASK,
      Capability.VIEW_TIME_CARD,
      Capability.VIEW_TIMESHEET,
      Capability.VIEW_USER,
      Capability.VIEW_USER_FULL,
      Capability.VIEW_VACATION_BALANCE,
      Capability.VIEW_WORKFLOW,
    ],
    scope: {
      [RoleScope.BELOW_ALL]: [
        Capability.APPROVE_ABSENCE,
        Capability.APPROVE_AS_HR,
        Capability.CREATE_ABSENCE,
        Capability.CREATE_ACKNOWLEDGEMENT,
        Capability.CREATE_CANDIDATE,
        Capability.CREATE_DOCUMENT,
        Capability.CREATE_EQUIPMENT,
        Capability.CREATE_JOB_APPLICATION,
        Capability.CREATE_JOB_OPENING,
        Capability.CREATE_NOTIFICATION,
        Capability.CREATE_PLANNING,
        Capability.CREATE_TASK,
        Capability.CREATE_USER,
        Capability.DELETE_TIME_CARD,
        Capability.DELETE_TIMESHEET,
        Capability.EDIT_ABSENCE,
        Capability.EDIT_ACKNOWLEDGEMENT,
        Capability.EDIT_CUSTOM_DATA,
        Capability.EDIT_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
        Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.EDIT_CUSTOM_DATA_JOB_APPLICATION,
        Capability.EDIT_DEV_EXPERIENCE_REGULAR,
        Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
        Capability.EDIT_DEV_REGULAR,
        Capability.EDIT_DEV_SENSITIVE,
        Capability.EDIT_DMGR_UNIT,
        Capability.EDIT_DOCUMENT,
        Capability.EDIT_EMP_BASIC,
        Capability.EDIT_EMP_CONTRACT_NUMBER,
        Capability.EDIT_EMP_EME,
        Capability.EDIT_EMP_FULL,
        Capability.EDIT_EMP_HOME,
        Capability.EDIT_EMP_INFO,
        Capability.EDIT_EMP_NUMBER,
        Capability.EDIT_EMP_OFFICE,
        Capability.EDIT_EMP_OTHER,
        Capability.EDIT_EMP_PROFILE,
        Capability.EDIT_EMP_PROFILE_INFO,
        Capability.EDIT_EMP_WORK,
        Capability.EDIT_EMPL_START,
        Capability.EDIT_EMPLOYMENT_BASIC,
        Capability.EDIT_EMPLOYMENT_DETAIL,
        Capability.EDIT_EMPLOYMENT_INFO,
        Capability.EDIT_EQUIPMENT,
        Capability.EDIT_JOB_APPLICATION,
        Capability.EDIT_JOB_OPENING,
        Capability.EDIT_NOTIFICATION,
        Capability.EDIT_PERFORMANCE,
        Capability.EDIT_PLANNING,
        Capability.EDIT_RESP_UNIT,
        Capability.EDIT_USER_LANGUAGE,
        Capability.EDIT_USER_SETTING,
        Capability.EDIT_VACATION_BALANCE,
        Capability.EDIT_WORKFLOW,
        Capability.MANAGE_TIME_CARD,
        Capability.REMOVE_ABSENCE,
        Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
        Capability.REMOVE_DEV_REGULAR,
        Capability.REMOVE_DOCUMENT,
        Capability.REMOVE_EQUIPMENT,
        Capability.REMOVE_JOB_APPLICATION,
        Capability.REMOVE_JOB_OPENING,
        Capability.REMOVE_NOTIFICATION,
        Capability.REMOVE_PERF_REGULAR,
        Capability.REMOVE_PLANNING,
        Capability.REMOVE_TASK,
        Capability.REMOVE_USER,
        Capability.UNREGISTER_FOR_COMPANY_TRAINING,
        Capability.VIEW_ABSENCE,
        Capability.VIEW_ACKNOWLEDGEMENT,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
        Capability.VIEW_CUSTOM_DATA_TIMESHEET,
        Capability.VIEW_DEV_EXPERIENCE,
        Capability.VIEW_DEVELOPMENT,
        Capability.VIEW_DMGR_EMPLOYEES,
        Capability.VIEW_DMGR_UNIT,
        Capability.VIEW_DOCUMENT,
        Capability.VIEW_EMP_BASIC,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_EME,
        Capability.VIEW_EMP_ENDED_EDITABLE,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_HOME,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMP_USERNAME,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_EQUIPMENT,
        Capability.VIEW_JOB_APPLICATION,
        Capability.VIEW_JOB_OPENING,
        Capability.VIEW_NOTIFICATION,
        Capability.VIEW_PERFORMANCE,
        Capability.VIEW_PLANNING,
        Capability.VIEW_REPORT_HR_APPRAISAL,
        Capability.VIEW_REPORT_HR_DECISION,
        Capability.VIEW_REPORT_HR_EARLY_INVOLVEMENT,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_TASK,
        Capability.VIEW_TIME_CARD,
        Capability.VIEW_TIMESHEET,
        Capability.VIEW_USER_FULL,
        Capability.VIEW_VACATION_BALANCE,
        Capability.VIEW_WORKFLOW,
      ],
      [RoleScope.ALL]: [
        Capability.APPROVE_EVALUATION,
        Capability.APPROVE_GOALS_EVALUATION,
        Capability.ARCHIVE_EVALUATION,
        Capability.ARCHIVE_GOALS_EVALUATION,
        Capability.CREATE_APPRAISAL,
        Capability.CREATE_CUSTOM_DATA_SCHEMA,
        Capability.CREATE_DECISION,
        Capability.CREATE_DEV_COMPANY,
        Capability.CREATE_EARLY_INVOLVEMENT,
        Capability.CREATE_EVALUATION,
        Capability.CREATE_GOALS,
        Capability.CREATE_GOALS_EVALUATION,
        Capability.CREATE_HOLIDAY,
        Capability.CREATE_MAIL_TEMPL,
        Capability.CREATE_TIMESHEET_SETTINGS,
        Capability.CREATE_WORKLIST,
        Capability.DELETE_APPRAISAL,
        Capability.DELETE_EARLY_INVOLVEMENT,
        Capability.DELETE_GOALS,
        Capability.EDIT_CANDIDATE,
        Capability.EDIT_COMPANY_CONFIG,
        Capability.EDIT_COMPANY_FILE,
        Capability.EDIT_COMPANY_TRAINING_PARTICIPANT,
        Capability.EDIT_CUSTOM_DATA_SCHEMA,
        Capability.EDIT_DEV_COMPANY,
        Capability.EDIT_ENUM,
        Capability.EDIT_HOLIDAY,
        Capability.EDIT_STRING,
        Capability.EDIT_TIMESHEET_SETTINGS,
        Capability.EDIT_WORKLIST,
        Capability.GRAPHQL,
        Capability.IS_HR_RESP,
        Capability.MANAGE_COMPANY_TRAINING_PARTICIPANTS,
        Capability.MANAGE_COMPANY_TRAININGS,
        Capability.MANAGE_ENUM_SCORE_GROUP,
        Capability.MANAGE_JOB_REQUIREMENT,
        Capability.REMOVE_CUSTOM_DATA_SCHEMA,
        Capability.REMOVE_DEV_COMPANY,
        Capability.REMOVE_HOLIDAY,
        Capability.REMOVE_MAIL_TEMPL,
        Capability.SEND_GROUP_EMAIL,
        Capability.SOLAPULSE_CREATE_SURVEY,
        Capability.SOLAPULSE_MANAGE_SURVEY,
        Capability.SOLAPULSE_VIEW_SURVEY,
        Capability.UPDATE_APPRAISAL,
        Capability.UPDATE_DECISION,
        Capability.UPDATE_EARLY_INVOLVEMENT,
        Capability.UPDATE_GOALS,
        Capability.VIEW_ALL_COMPANY_TRAININGS,
        Capability.VIEW_APPRAISAL,
        Capability.VIEW_APPRAISAL_REPORT,
        Capability.VIEW_CANDIDATE,
        Capability.VIEW_COMPANY_TRAINING_PARTICIPANTS,
        Capability.VIEW_DECISION,
        Capability.VIEW_DECISION_REPORT,
        Capability.VIEW_EARLY_INVOLVEMENT,
        Capability.VIEW_EARLY_INVOLVEMENT_REPORT,
        Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
        Capability.VIEW_ENUM_SCORE_GROUP,
        Capability.VIEW_EVALUATION,
        Capability.VIEW_GOALS,
        Capability.VIEW_GOALS_EVALUATION,
        Capability.VIEW_HR_UNIT,
        Capability.VIEW_JOB_REQUIREMENT,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
        Capability.VIEW_REPORT_LEGENT_BASIC,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_UNIT_BASIC,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
        Capability.VIEW_TARGETED_COMPANY_TRAININGS,
        Capability.VIEW_USER,
      ],
      [RoleScope.SELF]: [
        Capability.VIEW_REPORT_HR,
        Capability.VIEW_REPORT_SETTINGS,
      ],
    },
  },
  {
    name: Roles.HR_INITIAL,
    capabilities: [
      Capability.CREATE_EMPLOYEE,
      Capability.CREATE_EMPLOYMENT,
      Capability.CREATE_SECONDARY_EMPLOYMENT,
      Capability.CREATE_USER,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_USER_ROLES,
      Capability.GRAPHQL,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.CREATE_EMPLOYEE,
        Capability.CREATE_EMPLOYMENT,
        Capability.CREATE_SECONDARY_EMPLOYMENT,
        Capability.CREATE_USER,
        Capability.EDIT_EMP_WORK,
        Capability.EDIT_USER_ROLES,
        Capability.GRAPHQL,
      ],
    },
  },
  {
    name: Roles.HR_SALARY,
    capabilities: [
      Capability.APPROVE_AS_HR,
      Capability.CREATE_ABSENCE,
      Capability.CREATE_ACKNOWLEDGEMENT,
      Capability.CREATE_DOCUMENT,
      Capability.CREATE_EMP_PENDING,
      Capability.CREATE_EMPL_PENDING,
      Capability.CREATE_EMPLOYEE,
      Capability.CREATE_EMPLOYMENT,
      Capability.CREATE_EQUIPMENT,
      Capability.CREATE_REPORT_SETTINGS,
      Capability.CREATE_WORKFLOW,
      Capability.DELETE_REPORT_SETTINGS,
      Capability.EDIT_ACKNOWLEDGEMENT,
      Capability.EDIT_CUSTOM_DATA,
      Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
      Capability.EDIT_DEV_EXPERIENCE_REGULAR,
      Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
      Capability.EDIT_DOCUMENT,
      Capability.EDIT_EMP_BASIC,
      Capability.EDIT_EMP_CONTRACT_NUMBER,
      Capability.EDIT_EMP_EME,
      Capability.EDIT_EMP_FULL,
      Capability.EDIT_EMP_HOME,
      Capability.EDIT_EMP_NUMBER,
      Capability.EDIT_EMP_OFFICE,
      Capability.EDIT_EMP_OTHER,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_EMPLOYMENT,
      Capability.EDIT_EMPLOYMENT_BASIC,
      Capability.EDIT_ENUM,
      Capability.EDIT_EQUIPMENT,
      Capability.EDIT_VACATION_BALANCE,
      Capability.EDIT_WORKFLOW,
      Capability.HARD_REMOVE_ABSENCE,
      Capability.REMOVE_ABSENCE,
      Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
      Capability.REMOVE_DOCUMENT,
      Capability.REMOVE_EMPLOYMENT,
      Capability.REMOVE_EQUIPMENT,
      Capability.REMOVE_VACATION_BALANCE,
      Capability.UPDATE_REPORT_SETTINGS,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_ABSENCE_VACATION,
      Capability.VIEW_ACKNOWLEDGEMENT,
      Capability.VIEW_APPRAISAL_REPORT,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
      Capability.VIEW_CUSTOM_DATA_TIMESHEET,
      Capability.VIEW_DECISION_REPORT,
      Capability.VIEW_DEVELOPMENT,
      Capability.VIEW_DOCUMENT,
      Capability.VIEW_EARLY_INVOLVEMENT_REPORT,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_EQUIPMENT,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_COMPANY_SENSITIVE,
      Capability.VIEW_REPORT_HR,
      Capability.VIEW_REPORT_HR_SENSITIVE,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
      Capability.VIEW_SUMMARY_LEGENT,
      Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
      Capability.VIEW_TIME_CARD,
      Capability.VIEW_TIMESHEET_SETTINGS,
      Capability.VIEW_VACATION_BALANCE,
      Capability.VIEW_WORKFLOW,
    ],
    scope: {
      [RoleScope.BELOW_ALL]: [
        Capability.APPROVE_AS_HR,
        Capability.CREATE_ABSENCE,
        Capability.CREATE_ACKNOWLEDGEMENT,
        Capability.CREATE_DOCUMENT,
        Capability.CREATE_EMP_PENDING,
        Capability.CREATE_EMPL_PENDING,
        Capability.CREATE_EMPLOYEE,
        Capability.CREATE_EMPLOYMENT,
        Capability.CREATE_EQUIPMENT,
        Capability.CREATE_WORKFLOW,
        Capability.EDIT_ACKNOWLEDGEMENT,
        Capability.EDIT_CUSTOM_DATA,
        Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
        Capability.EDIT_DEV_EXPERIENCE_REGULAR,
        Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
        Capability.EDIT_DOCUMENT,
        Capability.EDIT_EMP_BASIC,
        Capability.EDIT_EMP_CONTRACT_NUMBER,
        Capability.EDIT_EMP_EME,
        Capability.EDIT_EMP_FULL,
        Capability.EDIT_EMP_HOME,
        Capability.EDIT_EMP_NUMBER,
        Capability.EDIT_EMP_OFFICE,
        Capability.EDIT_EMP_OTHER,
        Capability.EDIT_EMP_WORK,
        Capability.EDIT_EMPLOYMENT,
        Capability.EDIT_EMPLOYMENT_BASIC,
        Capability.EDIT_EQUIPMENT,
        Capability.EDIT_VACATION_BALANCE,
        Capability.EDIT_WORKFLOW,
        Capability.HARD_REMOVE_ABSENCE,
        Capability.REMOVE_ABSENCE,
        Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
        Capability.REMOVE_DOCUMENT,
        Capability.REMOVE_EMPLOYMENT,
        Capability.REMOVE_EQUIPMENT,
        Capability.REMOVE_VACATION_BALANCE,
        Capability.VIEW_ABSENCE,
        Capability.VIEW_ABSENCE_VACATION,
        Capability.VIEW_ACKNOWLEDGEMENT,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
        Capability.VIEW_CUSTOM_DATA_TIMESHEET,
        Capability.VIEW_DEVELOPMENT,
        Capability.VIEW_DOCUMENT,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_EQUIPMENT,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
        Capability.VIEW_TIME_CARD,
        Capability.VIEW_TIMESHEET_SETTINGS,
        Capability.VIEW_VACATION_BALANCE,
        Capability.VIEW_WORKFLOW,
      ],
      [RoleScope.SELF]: [
        Capability.CREATE_REPORT_SETTINGS,
        Capability.DELETE_REPORT_SETTINGS,
        Capability.UPDATE_REPORT_SETTINGS,
        Capability.VIEW_REPORT_HR,
        Capability.VIEW_REPORT_HR_SENSITIVE,
        Capability.VIEW_REPORT_SETTINGS,
      ],
      [RoleScope.ALL]: [
        Capability.EDIT_ENUM,
        Capability.VIEW_APPRAISAL_REPORT,
        Capability.VIEW_DECISION_REPORT,
        Capability.VIEW_EARLY_INVOLVEMENT_REPORT,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
        Capability.VIEW_REPORT_COMPANY_SENSITIVE,
        Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
        Capability.VIEW_SUMMARY_LEGENT,
        Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
      ],
    },
  },
  {
    name: Roles.HR_STAFF,
    capabilities: [
      Capability.CREATE_BONUS_CALC_RULE,
      Capability.CREATE_EARLY_INVOLVEMENT,
      Capability.CREATE_EMPLOYEE,
      Capability.CREATE_EMPLOYMENT,
      Capability.CREATE_OFFICE,
      Capability.CREATE_REPORT_SETTINGS,
      Capability.CREATE_SECONDARY_EMPLOYMENT,
      Capability.CREATE_TREE,
      Capability.CREATE_WORKFLOW,
      Capability.DELETE_EARLY_INVOLVEMENT,
      Capability.DELETE_REPORT_SETTINGS,
      Capability.EDIT_BONUS_CALC_RULE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
      Capability.EDIT_DOC_APPROVAL,
      Capability.EDIT_EMP_CONTRACT_NUMBER,
      Capability.EDIT_EMPL_BANKACCOUNT,
      Capability.EDIT_EMPLOYMENT,
      Capability.EDIT_MAIL_TEMPL,
      Capability.EDIT_OFFICE,
      Capability.EDIT_ORGUNIT,
      Capability.EDIT_TREE,
      Capability.EDIT_USER_ROLES,
      Capability.EDIT_USER_STATUS,
      Capability.EDIT_USER_USERNAME,
      Capability.EXEC_EMAIL,
      Capability.GRAPHQL,
      Capability.HARD_REMOVE_CUSTOM_DATA_SCHEMA,
      Capability.MANAGE_TRANSLATIONS,
      Capability.REMOVE_BONUS_CALC_RULE,
      Capability.REMOVE_EMP_PROFILE,
      Capability.REMOVE_OFFICE,
      Capability.REMOVE_ORGUNIT,
      Capability.REMOVE_TREE,
      Capability.SEND_DOC_APPROVAL,
      Capability.SOLAINTEL_CREATE_INDICATOR,
      Capability.SOLAINTEL_DELETE_INDICATOR,
      Capability.SOLAINTEL_EDIT_INDICATOR,
      Capability.SOLAINTEL_VIEW_INDICATOR,
      Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
      Capability.UPDATE_EARLY_INVOLVEMENT,
      Capability.UPDATE_REPORT_SETTINGS,
      Capability.VIEW_BONUS_CALC_RULE,
      Capability.VIEW_COMPANY_SENSITIVE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
      Capability.VIEW_CUSTOM_DATA_SCHEMA,
      Capability.VIEW_DOC_APPROVAL,
      Capability.VIEW_EARLY_INVOLVEMENT,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_EMPLOYMENT_BASIC_REHIRE,
      Capability.VIEW_MAIL_TEMPL,
      Capability.VIEW_REPORT_COMPANY_SENSITIVE,
      Capability.VIEW_REPORT_HR_APPRAISAL,
      Capability.VIEW_REPORT_HR_DECISION,
      Capability.VIEW_REPORT_HR_EARLY_INVOLVEMENT,
      Capability.VIEW_REPORT_HR_SENSITIVE,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
      Capability.VIEW_SUMMARY_LEGENT,
      Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.CREATE_BONUS_CALC_RULE,
        Capability.CREATE_EARLY_INVOLVEMENT,
        Capability.CREATE_OFFICE,
        Capability.CREATE_TREE,
        Capability.DELETE_EARLY_INVOLVEMENT,
        Capability.EDIT_BONUS_CALC_RULE,
        Capability.EDIT_DOC_APPROVAL,
        Capability.EDIT_MAIL_TEMPL,
        Capability.EDIT_OFFICE,
        Capability.EDIT_TREE,
        Capability.EXEC_EMAIL,
        Capability.GRAPHQL,
        Capability.MANAGE_TRANSLATIONS,
        Capability.REMOVE_BONUS_CALC_RULE,
        Capability.REMOVE_OFFICE,
        Capability.REMOVE_TREE,
        Capability.SEND_DOC_APPROVAL,
        Capability.SOLAINTEL_CREATE_INDICATOR,
        Capability.SOLAINTEL_DELETE_INDICATOR,
        Capability.SOLAINTEL_EDIT_INDICATOR,
        Capability.SOLAINTEL_VIEW_INDICATOR,
        Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
        Capability.UPDATE_EARLY_INVOLVEMENT,
        Capability.VIEW_BONUS_CALC_RULE,
        Capability.VIEW_COMPANY_SENSITIVE,
        Capability.VIEW_DOC_APPROVAL,
        Capability.VIEW_EARLY_INVOLVEMENT,
        Capability.VIEW_EMPLOYMENT_BASIC_REHIRE,
        Capability.VIEW_MAIL_TEMPL,
        Capability.VIEW_REPORT_COMPANY_SENSITIVE,
        Capability.VIEW_SUMMARY_COMPANY_SENSITIVE,
        Capability.VIEW_SUMMARY_LEGENT,
        Capability.VIEW_SUMMARY_LEGENT_SENSITIVE,
      ],
      [RoleScope.BELOW_ALL]: [
        Capability.CREATE_EMPLOYEE,
        Capability.CREATE_EMPLOYMENT,
        Capability.CREATE_SECONDARY_EMPLOYMENT,
        Capability.CREATE_WORKFLOW,
        Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
        Capability.EDIT_EMP_CONTRACT_NUMBER,
        Capability.EDIT_EMPL_BANKACCOUNT,
        Capability.EDIT_EMPLOYMENT,
        Capability.EDIT_ORGUNIT,
        Capability.EDIT_USER_ROLES,
        Capability.EDIT_USER_STATUS,
        Capability.EDIT_USER_USERNAME,
        Capability.HARD_REMOVE_CUSTOM_DATA_SCHEMA,
        Capability.REMOVE_EMP_PROFILE,
        Capability.REMOVE_ORGUNIT,
        Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
        Capability.VIEW_CUSTOM_DATA_SCHEMA,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_REPORT_HR_APPRAISAL,
        Capability.VIEW_REPORT_HR_DECISION,
        Capability.VIEW_REPORT_HR_EARLY_INVOLVEMENT,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
      ],
      [RoleScope.SELF]: [
        Capability.CREATE_REPORT_SETTINGS,
        Capability.DELETE_REPORT_SETTINGS,
        Capability.UPDATE_REPORT_SETTINGS,
        Capability.VIEW_REPORT_HR_SENSITIVE,
        Capability.VIEW_REPORT_SETTINGS,
      ],
    },
  },
  {
    name: Roles.IT_ADMIN,
    capabilities: [
      Capability.CREATE_EQUIPMENT,
      Capability.CREATE_TASK,
      Capability.CREATE_USER,
      Capability.EDIT_CUSTOM_DATA,
      Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.EDIT_EMP_NUMBER,
      Capability.EDIT_EMP_OFFICE,
      Capability.EDIT_EMP_PROFILE,
      Capability.EDIT_EQUIPMENT,
      Capability.EDIT_STRING,
      Capability.EDIT_USER_LANGUAGE,
      Capability.EDIT_USER_STATUS,
      Capability.EDIT_USER_USERNAME,
      Capability.EXEC_EMAIL,
      Capability.GRAPHQL,
      Capability.IS_ITADMIN_RESP,
      Capability.REMOVE_EMP_PROFILE,
      Capability.REMOVE_EQUIPMENT,
      Capability.REMOVE_USER,
      Capability.SEND_GROUP_EMAIL,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_ENDED_EDITABLE,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_HOME,
      Capability.VIEW_EMP_USERNAME,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_EQUIPMENT,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_LEGENT_BASIC,
      Capability.VIEW_REPORT_UNIT_BASIC,
      Capability.VIEW_TASK,
      Capability.VIEW_USER,
      Capability.VIEW_USER_FULL,
      Capability.VIEW_USER_LOGIN_INFO,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.CREATE_EQUIPMENT,
        Capability.CREATE_TASK,
        Capability.CREATE_USER,
        Capability.EDIT_CUSTOM_DATA,
        Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.EDIT_EMP_NUMBER,
        Capability.EDIT_EMP_OFFICE,
        Capability.EDIT_EMP_PROFILE,
        Capability.EDIT_EQUIPMENT,
        Capability.EDIT_STRING,
        Capability.EDIT_USER_LANGUAGE,
        Capability.EDIT_USER_STATUS,
        Capability.EDIT_USER_USERNAME,
        Capability.EXEC_EMAIL,
        Capability.GRAPHQL,
        Capability.IS_ITADMIN_RESP,
        Capability.REMOVE_EMP_PROFILE,
        Capability.REMOVE_EQUIPMENT,
        Capability.REMOVE_USER,
        Capability.SEND_GROUP_EMAIL,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_ENDED_EDITABLE,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_HOME,
        Capability.VIEW_EMP_USERNAME,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_EQUIPMENT,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
        Capability.VIEW_REPORT_LEGENT_BASIC,
        Capability.VIEW_REPORT_UNIT_BASIC,
        Capability.VIEW_TASK,
        Capability.VIEW_USER,
        Capability.VIEW_USER_FULL,
        Capability.VIEW_USER_LOGIN_INFO,
      ],
    },
  },
  {
    name: Roles.MANAGER,
    capabilities: [
      Capability.APPROVE_ABSENCE,
      Capability.APPROVE_AS_MANAGER,
      Capability.APPROVE_EVALUATION,
      Capability.APPROVE_GOALS_EVALUATION,
      Capability.ARCHIVE_EVALUATION,
      Capability.ARCHIVE_GOALS_EVALUATION,
      Capability.CREATE_ABSENCE,
      Capability.CREATE_ACKNOWLEDGEMENT,
      Capability.CREATE_CANDIDATE,
      Capability.CREATE_DECISION,
      Capability.CREATE_DOCUMENT,
      Capability.CREATE_EMP_PENDING,
      Capability.CREATE_EMPL_PENDING,
      Capability.CREATE_EQUIPMENT,
      Capability.CREATE_EVALUATION,
      Capability.CREATE_GOALS_EVALUATION,
      Capability.CREATE_JOB_APPLICATION,
      Capability.CREATE_JOB_OPENING,
      Capability.CREATE_ORGUNIT,
      Capability.CREATE_PLANNING,
      Capability.CREATE_REPORT_SETTINGS,
      Capability.CREATE_SECONDARY_EMPLOYMENT,
      Capability.CREATE_TASK,
      Capability.CREATE_WORKFLOW,
      Capability.DELETE_REPORT_SETTINGS,
      Capability.DELETE_TIME_CARD,
      Capability.EDIT_ABSENCE,
      Capability.EDIT_ACKNOWLEDGEMENT,
      Capability.EDIT_CANDIDATE,
      Capability.EDIT_COMPANY_TRAINING_PARTICIPANT,
      Capability.EDIT_CUSTOM_DATA,
      Capability.EDIT_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
      Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.EDIT_CUSTOM_DATA_JOB_APPLICATION,
      Capability.EDIT_DEV_EXPERIENCE_REGULAR,
      Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
      Capability.EDIT_DEV_REGULAR,
      Capability.EDIT_DEV_SENSITIVE,
      Capability.EDIT_DMGR_UNIT,
      Capability.EDIT_DOC_APPROVAL,
      Capability.EDIT_DOCUMENT,
      Capability.EDIT_EMP_FULL,
      Capability.EDIT_EMP_HOME,
      Capability.EDIT_EMP_INFO,
      Capability.EDIT_EMP_OFFICE,
      Capability.EDIT_EMP_OTHER,
      Capability.EDIT_EMP_PROFILE_INFO,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_EMPL_BANKACCOUNT,
      Capability.EDIT_EMPLOYMENT,
      Capability.EDIT_EMPLOYMENT_BASIC,
      Capability.EDIT_EMPLOYMENT_DETAIL,
      Capability.EDIT_EMPLOYMENT_INFO,
      Capability.EDIT_EQUIPMENT,
      Capability.EDIT_JOB_APPLICATION,
      Capability.EDIT_JOB_OPENING,
      Capability.EDIT_MGR_NOTE,
      Capability.EDIT_MGR_NOTE_EMP,
      Capability.EDIT_ORGUNIT,
      Capability.EDIT_PERFORMANCE,
      Capability.EDIT_PLANNING,
      Capability.EDIT_RESP_UNIT,
      Capability.EDIT_WORKFLOW,
      Capability.GRAPHQL,
      Capability.IS_MGR_RESP,
      Capability.MANAGE_JOB_REQUIREMENT,
      Capability.MANAGE_TIME_CARD,
      Capability.MANAGE_TRANSLATIONS,
      Capability.REMOVE_ABSENCE,
      Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
      Capability.REMOVE_DEV_REGULAR,
      Capability.REMOVE_EQUIPMENT,
      Capability.REMOVE_JOB_OPENING,
      Capability.REMOVE_MGR_NOTE,
      Capability.REMOVE_ORGUNIT,
      Capability.REMOVE_PERF_REGULAR,
      Capability.REMOVE_PLANNING,
      Capability.SEND_DOC_APPROVAL,
      Capability.SEND_GROUP_EMAIL,
      Capability.SOLAINTEL_CREATE_INDICATOR,
      Capability.SOLAINTEL_DELETE_INDICATOR,
      Capability.SOLAINTEL_EDIT_INDICATOR,
      Capability.SOLAINTEL_VIEW_INDICATOR,
      Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
      Capability.SOLAPULSE_CREATE_SURVEY,
      Capability.SOLAPULSE_MANAGE_SURVEY,
      Capability.SOLAPULSE_VIEW_SURVEY,
      Capability.UNREGISTER_FOR_COMPANY_TRAINING,
      Capability.UPDATE_DECISION,
      Capability.UPDATE_REPORT_SETTINGS,
      Capability.VIEW_ABSENCE,
      Capability.VIEW_ACKNOWLEDGEMENT,
      Capability.VIEW_APPRAISAL,
      Capability.VIEW_APPRAISAL_REPORT,
      Capability.VIEW_BONUS_CALC_RULE,
      Capability.VIEW_CANDIDATE,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
      Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
      Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
      Capability.VIEW_CUSTOM_DATA_TIMESHEET,
      Capability.VIEW_DECISION,
      Capability.VIEW_DECISION_REPORT,
      Capability.VIEW_DEV_EXPERIENCE,
      Capability.VIEW_DEVELOPMENT,
      Capability.VIEW_DMGR_EMPLOYEES,
      Capability.VIEW_DMGR_UNIT,
      Capability.VIEW_DOC_APPROVAL,
      Capability.VIEW_DOCUMENT,
      Capability.VIEW_EARLY_INVOLVEMENT,
      Capability.VIEW_EARLY_INVOLVEMENT_REPORT,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_EME,
      Capability.VIEW_EMP_ENDED_EDITABLE,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_HOME,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_EMPLOYMENT_BASIC_REHIRE,
      Capability.VIEW_EQUIPMENT,
      Capability.VIEW_EVALUATION,
      Capability.VIEW_GOALS,
      Capability.VIEW_GOALS_EVALUATION,
      Capability.VIEW_JOB_APPLICATION,
      Capability.VIEW_JOB_OPENING,
      Capability.VIEW_JOB_REQUIREMENT,
      Capability.VIEW_MAIL_TEMPL,
      Capability.VIEW_MGR_NOTE,
      Capability.VIEW_MGR_NOTE_EMP,
      Capability.VIEW_PERFORMANCE,
      Capability.VIEW_PLANNING,
      Capability.VIEW_REPORT_COMPANY,
      Capability.VIEW_REPORT_COMPANY_BASIC,
      Capability.VIEW_REPORT_LEGENT,
      Capability.VIEW_REPORT_LEGENT_BASIC,
      Capability.VIEW_REPORT_LEGENT_SENSITIVE,
      Capability.VIEW_REPORT_MANAGER,
      Capability.VIEW_REPORT_MANAGER_APPRAISAL,
      Capability.VIEW_REPORT_MANAGER_DECISION,
      Capability.VIEW_REPORT_MANAGER_EARLY_INVOLVEMENT,
      Capability.VIEW_REPORT_MANAGER_SENSITIVE,
      Capability.VIEW_REPORT_SETTINGS,
      Capability.VIEW_REPORT_UNIT,
      Capability.VIEW_REPORT_UNIT_BASIC,
      Capability.VIEW_REPORT_UNIT_SENSITIVE,
      Capability.VIEW_TARGETED_COMPANY_TRAININGS,
      Capability.VIEW_TASK,
      Capability.VIEW_TIME_CARD,
      Capability.VIEW_TIMESHEET,
      Capability.VIEW_VACATION_BALANCE,
      Capability.VIEW_WORKFLOW,
    ],
    scope: {
      [RoleScope.BELOW]: [
        Capability.APPROVE_ABSENCE,
        Capability.APPROVE_AS_MANAGER,
        Capability.APPROVE_EVALUATION,
        Capability.APPROVE_GOALS_EVALUATION,
        Capability.ARCHIVE_EVALUATION,
        Capability.ARCHIVE_GOALS_EVALUATION,
        Capability.CREATE_ABSENCE,
        Capability.CREATE_ACKNOWLEDGEMENT,
        Capability.CREATE_CANDIDATE,
        Capability.CREATE_DECISION,
        Capability.CREATE_DOCUMENT,
        Capability.CREATE_EMP_PENDING,
        Capability.CREATE_EMPL_PENDING,
        Capability.CREATE_EQUIPMENT,
        Capability.CREATE_EVALUATION,
        Capability.CREATE_GOALS_EVALUATION,
        Capability.CREATE_JOB_APPLICATION,
        Capability.CREATE_JOB_OPENING,
        Capability.CREATE_PLANNING,
        Capability.CREATE_REPORT_SETTINGS,
        Capability.CREATE_SECONDARY_EMPLOYMENT,
        Capability.CREATE_TASK,
        Capability.CREATE_WORKFLOW,
        Capability.DELETE_REPORT_SETTINGS,
        Capability.DELETE_TIME_CARD,
        Capability.EDIT_ABSENCE,
        Capability.EDIT_ACKNOWLEDGEMENT,
        Capability.EDIT_CANDIDATE,
        Capability.EDIT_CUSTOM_DATA,
        Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.EDIT_DEV_EXPERIENCE_REGULAR,
        Capability.EDIT_DEV_EXPERIENCE_SENSITIVE,
        Capability.EDIT_DEV_REGULAR,
        Capability.EDIT_DEV_SENSITIVE,
        Capability.EDIT_DMGR_UNIT,
        Capability.EDIT_DOCUMENT,
        Capability.EDIT_EMP_FULL,
        Capability.EDIT_EMP_HOME,
        Capability.EDIT_EMP_INFO,
        Capability.EDIT_EMP_OFFICE,
        Capability.EDIT_EMP_OTHER,
        Capability.EDIT_EMP_PROFILE_INFO,
        Capability.EDIT_EMP_WORK,
        Capability.EDIT_EMPL_BANKACCOUNT,
        Capability.EDIT_EMPLOYMENT,
        Capability.EDIT_EMPLOYMENT_BASIC,
        Capability.EDIT_EMPLOYMENT_DETAIL,
        Capability.EDIT_EMPLOYMENT_INFO,
        Capability.EDIT_EQUIPMENT,
        Capability.EDIT_JOB_APPLICATION,
        Capability.EDIT_JOB_OPENING,
        Capability.EDIT_MGR_NOTE_EMP,
        Capability.EDIT_ORGUNIT,
        Capability.EDIT_PERFORMANCE,
        Capability.EDIT_PLANNING,
        Capability.EDIT_RESP_UNIT,
        Capability.EDIT_WORKFLOW,
        Capability.MANAGE_JOB_REQUIREMENT,
        Capability.MANAGE_TIME_CARD,
        Capability.REMOVE_ABSENCE,
        Capability.REMOVE_DEV_EXPERIENCE_REGULAR,
        Capability.REMOVE_DEV_REGULAR,
        Capability.REMOVE_EQUIPMENT,
        Capability.REMOVE_JOB_OPENING,
        Capability.REMOVE_ORGUNIT,
        Capability.REMOVE_PERF_REGULAR,
        Capability.REMOVE_PLANNING,
        Capability.SOLAINTEL_CREATE_INDICATOR,
        Capability.SOLAINTEL_DELETE_INDICATOR,
        Capability.SOLAINTEL_EDIT_INDICATOR,
        Capability.SOLAINTEL_VIEW_INDICATOR,
        Capability.SOLAINTEL_VIEW_INDICATOR_RESULTS,
        Capability.SOLAPULSE_CREATE_SURVEY,
        Capability.SOLAPULSE_MANAGE_SURVEY,
        Capability.SOLAPULSE_VIEW_SURVEY,
        Capability.UNREGISTER_FOR_COMPANY_TRAINING,
        Capability.UPDATE_DECISION,
        Capability.UPDATE_REPORT_SETTINGS,
        Capability.VIEW_ABSENCE,
        Capability.VIEW_ACKNOWLEDGEMENT,
        Capability.VIEW_APPRAISAL,
        Capability.VIEW_APPRAISAL_REPORT,
        Capability.VIEW_CANDIDATE,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_CUSTOM_DATA_EMPLOYMENT,
        Capability.VIEW_CUSTOM_DATA_TIMESHEET,
        Capability.VIEW_DECISION,
        Capability.VIEW_DECISION_REPORT,
        Capability.VIEW_DEV_EXPERIENCE,
        Capability.VIEW_DEVELOPMENT,
        Capability.VIEW_DMGR_EMPLOYEES,
        Capability.VIEW_DMGR_UNIT,
        Capability.VIEW_DOCUMENT,
        Capability.VIEW_EARLY_INVOLVEMENT,
        Capability.VIEW_EARLY_INVOLVEMENT_REPORT,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_EME,
        Capability.VIEW_EMP_ENDED_EDITABLE,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_HOME,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMPLOYEE_COMPANY_TRAININGS,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_EQUIPMENT,
        Capability.VIEW_EVALUATION,
        Capability.VIEW_GOALS,
        Capability.VIEW_GOALS_EVALUATION,
        Capability.VIEW_JOB_APPLICATION,
        Capability.VIEW_JOB_OPENING,
        Capability.VIEW_JOB_REQUIREMENT,
        Capability.VIEW_MGR_NOTE_EMP,
        Capability.VIEW_PERFORMANCE,
        Capability.VIEW_PLANNING,
        Capability.VIEW_REPORT_LEGENT,
        Capability.VIEW_REPORT_LEGENT_BASIC,
        Capability.VIEW_REPORT_LEGENT_SENSITIVE,
        Capability.VIEW_REPORT_MANAGER,
        Capability.VIEW_REPORT_MANAGER_APPRAISAL,
        Capability.VIEW_REPORT_MANAGER_DECISION,
        Capability.VIEW_REPORT_MANAGER_EARLY_INVOLVEMENT,
        Capability.VIEW_REPORT_MANAGER_SENSITIVE,
        Capability.VIEW_REPORT_SETTINGS,
        Capability.VIEW_REPORT_UNIT,
        Capability.VIEW_REPORT_UNIT_BASIC,
        Capability.VIEW_REPORT_UNIT_SENSITIVE,
        Capability.VIEW_TARGETED_COMPANY_TRAININGS,
        Capability.VIEW_TASK,
        Capability.VIEW_TIME_CARD,
        Capability.VIEW_TIMESHEET,
        Capability.VIEW_VACATION_BALANCE,
        Capability.VIEW_WORKFLOW,
      ],
      [RoleScope.ALL]: [
        Capability.CREATE_ORGUNIT,
        Capability.EDIT_COMPANY_TRAINING_PARTICIPANT,
        Capability.GRAPHQL,
        Capability.IS_MGR_RESP,
        Capability.MANAGE_TRANSLATIONS,
        Capability.SEND_GROUP_EMAIL,
        Capability.VIEW_BONUS_CALC_RULE,
        Capability.VIEW_EMPLOYMENT_BASIC_REHIRE,
        Capability.VIEW_MAIL_TEMPL,
        Capability.VIEW_REPORT_COMPANY,
        Capability.VIEW_REPORT_COMPANY_BASIC,
      ],
      [RoleScope.BELOW_ALL]: [
        Capability.EDIT_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
        Capability.EDIT_CUSTOM_DATA_JOB_APPLICATION,
        Capability.EDIT_DOC_APPROVAL,
        Capability.SEND_DOC_APPROVAL,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PAID,
        Capability.VIEW_CUSTOM_DATA_BILLABLE_HOURS_PROPOSAL,
        Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
        Capability.VIEW_DOC_APPROVAL,
      ],
      [RoleScope.SELF]: [
        Capability.EDIT_MGR_NOTE,
        Capability.REMOVE_MGR_NOTE,
        Capability.VIEW_MGR_NOTE,
      ],
    },
  },
  {
    name: Roles.RECRUITER,
    capabilities: [
      Capability.APPROVE_AS_HR,
      Capability.CREATE_ACKNOWLEDGEMENT,
      Capability.CREATE_CUSTOM_DATA_SCHEMA,
      Capability.CREATE_DOCUMENT,
      Capability.CREATE_EMP_PENDING,
      Capability.CREATE_EMPL_PENDING,
      Capability.CREATE_EMPL_RESTRICTED,
      Capability.CREATE_EMPLOYEE,
      Capability.CREATE_EMPLOYMENT,
      Capability.CREATE_EQUIPMENT,
      Capability.CREATE_JOB_APP_PUBLIC,
      Capability.CREATE_JOB_APPLICATION,
      Capability.CREATE_JOB_OPENING,
      Capability.CREATE_MAIL_TEMPL,
      Capability.CREATE_SECONDARY_EMPLOYMENT,
      Capability.CREATE_WORKFLOW,
      Capability.EDIT_ACKNOWLEDGEMENT,
      Capability.EDIT_COMPANY_CONFIG,
      Capability.EDIT_COMPANY_FILE,
      Capability.EDIT_CUSTOM_DATA,
      Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
      Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
      Capability.EDIT_CUSTOM_DATA_SCHEMA,
      Capability.EDIT_DOCUMENT,
      Capability.EDIT_EMP_BASIC,
      Capability.EDIT_EMP_EME,
      Capability.EDIT_EMP_FULL,
      Capability.EDIT_EMP_HOME,
      Capability.EDIT_EMP_INFO,
      Capability.EDIT_EMP_OFFICE,
      Capability.EDIT_EMP_OTHER,
      Capability.EDIT_EMP_WORK,
      Capability.EDIT_EMPL_BANKACCOUNT,
      Capability.EDIT_EMPLOYMENT,
      Capability.EDIT_EMPLOYMENT_BASIC,
      Capability.EDIT_EMPLOYMENT_DETAIL,
      Capability.EDIT_EMPLOYMENT_INFO,
      Capability.EDIT_ENUM,
      Capability.EDIT_EQUIPMENT,
      Capability.EDIT_JOB_APPLICATION,
      Capability.EDIT_JOB_OPENING,
      Capability.EDIT_MAIL_TEMPL,
      Capability.EDIT_STRING,
      Capability.EDIT_WORKFLOW,
      Capability.HARD_REMOVE_CUSTOM_DATA_SCHEMA,
      Capability.HARD_REMOVE_JOB_OPENING,
      Capability.REMOVE_CUSTOM_DATA_SCHEMA,
      Capability.REMOVE_EMPLOYMENT,
      Capability.REMOVE_ENUM,
      Capability.REMOVE_JOB_APPLICATION,
      Capability.REMOVE_JOB_OPENING,
      Capability.REMOVE_MAIL_TEMPL,
      Capability.VIEW_COMPANY,
      Capability.VIEW_COMPANY_FILE,
      Capability.VIEW_CUSTOM_DATA,
      Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
      Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
      Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
      Capability.VIEW_CUSTOM_DATA_SCHEMA,
      Capability.VIEW_EMP_BIRTHDAY,
      Capability.VIEW_EMP_ENDED_EDITABLE,
      Capability.VIEW_EMP_FILTERED,
      Capability.VIEW_EMP_SSN,
      Capability.VIEW_EMPLOYMENT,
      Capability.VIEW_EMPLOYMENT_BASIC,
      Capability.VIEW_ENUM,
      Capability.VIEW_JOB_APPLICATION,
      Capability.VIEW_JOB_OPENING,
      Capability.VIEW_JOB_OPENING_PUBLIC,
      Capability.VIEW_MAIL_TEMPL,
      Capability.VIEW_STRING,
      Capability.VIEW_WORKFLOW,
    ],
    scope: {
      [RoleScope.ALL]: [
        Capability.APPROVE_AS_HR,
        Capability.CREATE_ACKNOWLEDGEMENT,
        Capability.CREATE_CUSTOM_DATA_SCHEMA,
        Capability.CREATE_DOCUMENT,
        Capability.CREATE_EMP_PENDING,
        Capability.CREATE_EMPL_PENDING,
        Capability.CREATE_EMPL_RESTRICTED,
        Capability.CREATE_EMPLOYEE,
        Capability.CREATE_EMPLOYMENT,
        Capability.CREATE_EQUIPMENT,
        Capability.CREATE_JOB_APP_PUBLIC,
        Capability.CREATE_JOB_APPLICATION,
        Capability.CREATE_JOB_OPENING,
        Capability.CREATE_MAIL_TEMPL,
        Capability.CREATE_SECONDARY_EMPLOYMENT,
        Capability.CREATE_WORKFLOW,
        Capability.EDIT_ACKNOWLEDGEMENT,
        Capability.EDIT_COMPANY_CONFIG,
        Capability.EDIT_COMPANY_FILE,
        Capability.EDIT_CUSTOM_DATA,
        Capability.EDIT_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE,
        Capability.EDIT_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.EDIT_CUSTOM_DATA_EMPLOYMENT,
        Capability.EDIT_CUSTOM_DATA_SCHEMA,
        Capability.EDIT_DOCUMENT,
        Capability.EDIT_EMP_BASIC,
        Capability.EDIT_EMP_EME,
        Capability.EDIT_EMP_FULL,
        Capability.EDIT_EMP_HOME,
        Capability.EDIT_EMP_INFO,
        Capability.EDIT_EMP_OFFICE,
        Capability.EDIT_EMP_OTHER,
        Capability.EDIT_EMP_WORK,
        Capability.EDIT_EMPL_BANKACCOUNT,
        Capability.EDIT_EMPLOYMENT,
        Capability.EDIT_EMPLOYMENT_BASIC,
        Capability.EDIT_EMPLOYMENT_DETAIL,
        Capability.EDIT_EMPLOYMENT_INFO,
        Capability.EDIT_ENUM,
        Capability.EDIT_EQUIPMENT,
        Capability.EDIT_JOB_APPLICATION,
        Capability.EDIT_JOB_OPENING,
        Capability.EDIT_MAIL_TEMPL,
        Capability.EDIT_STRING,
        Capability.EDIT_WORKFLOW,
        Capability.HARD_REMOVE_CUSTOM_DATA_SCHEMA,
        Capability.HARD_REMOVE_JOB_OPENING,
        Capability.REMOVE_CUSTOM_DATA_SCHEMA,
        Capability.REMOVE_EMPLOYMENT,
        Capability.REMOVE_ENUM,
        Capability.REMOVE_JOB_APPLICATION,
        Capability.REMOVE_JOB_OPENING,
        Capability.REMOVE_MAIL_TEMPL,
        Capability.VIEW_COMPANY,
        Capability.VIEW_COMPANY_FILE,
        Capability.VIEW_CUSTOM_DATA,
        Capability.VIEW_CUSTOM_DATA_DECISION_REGISTRY,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE,
        Capability.VIEW_CUSTOM_DATA_EMPLOYEE_POS,
        Capability.VIEW_CUSTOM_DATA_JOB_APPLICATION,
        Capability.VIEW_CUSTOM_DATA_SCHEMA,
        Capability.VIEW_EMP_BIRTHDAY,
        Capability.VIEW_EMP_ENDED_EDITABLE,
        Capability.VIEW_EMP_FILTERED,
        Capability.VIEW_EMP_SSN,
        Capability.VIEW_EMPLOYMENT,
        Capability.VIEW_EMPLOYMENT_BASIC,
        Capability.VIEW_ENUM,
        Capability.VIEW_JOB_APPLICATION,
        Capability.VIEW_JOB_OPENING,
        Capability.VIEW_JOB_OPENING_PUBLIC,
        Capability.VIEW_MAIL_TEMPL,
        Capability.VIEW_STRING,
        Capability.VIEW_WORKFLOW,
      ],
    },
  },
].reduce((acc: RoleCapabilityCombo[], role: RoleCapabilityCombo) => {
  if (role.name === Roles.HR_ADMIN) {
    // Copy HR_ADMIN capabilities for HR_ADMIN_LIMITED
    return [...acc, role, { ...role, name: Roles.HR_ADMIN_LIMITED }];
  }
  return [...acc, role];
}, []);
